/* iva-quiz.js - quiz flow engine UI (IVATHERM-QUIZ.md §8.1, Typology-derived).
   Portable: ships unchanged to the theme. Host supplies window.IvaQuizConfig:
   { apiBase, channel, settings:{...copy}, media:{questionId:{img,alt}}, cart:{add(items)} }
   ALL branching comes from quiz-definition data; nothing question-specific is coded here. */
// Idempotent boot: the theme editor re-renders sections (shopify:section:load), so the host can
// call window.__ivaQuizBoot() again on a fresh mount. A booted mount is never double-wired.
window.__ivaQuizBoot = function () {
  "use strict";
  const cfg = window.IvaQuizConfig;
  const root = document.getElementById("iva-quiz");
  if (!cfg || !root || root.dataset.ivaBooted) return;
  root.dataset.ivaBooted = "1";

  // Entry-point attribution (IVATHERM-QUIZ-ANALYTICS.md §5). Read ONCE at boot from the URL.
  // Self-service (1 Sep 2026): accept ANY clean custom tag matching /^[a-z0-9_]{1,40}$/ so the
  // team can invent entry-point names freely (documented on the dashboard); anything else (typo,
  // junk, injection) degrades to "direct". The server (quiz-events) enforces the same rule. The
  // URL can't change within a page load, so this boot constant equals a press-time read; and
  // quiz_started only fires from the landing start button, so the tagged event stands for its run.
  const ENTRY_RE = /^[a-z0-9_]{1,40}$/;
  const entryPoint = (() => {
    try {
      const v = new URLSearchParams(location.search).get("entry_point");
      return v && ENTRY_RE.test(v) ? v : null;
    } catch { return null; }
  })();

  // PRD 6.3: autosave locally after every answer, so a refresh or a dropped connection never
  // loses the run. Answers stay on the device; nothing medical is written to the URL.
  const STORE_KEY = "iva-quiz-progress-v1";
  const STORE_TTL_MS = 24 * 3600 * 1000;
  function saveLocal() {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({
        v: S.versions?.question_set_version, at: Date.now(),
        answers: S.answers, current: S.current, trail: S.trail, session: S.sessionKey,
      }));
    } catch { /* private mode: autosave is best-effort */ }
  }
  function loadLocal() {
    try {
      const raw = JSON.parse(localStorage.getItem(STORE_KEY) || "null");
      if (!raw || Date.now() - raw.at > STORE_TTL_MS) return null;
      if (raw.v && S.versions?.question_set_version && raw.v !== S.versions.question_set_version) return null;
      return raw;
    } catch { return null; }
  }
  function clearLocal() { try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ } }

  // crypto.randomUUID is absent on Safari <15.4 and in any non-secure context. The old fallback
  // was String(Math.random()), which is NOT a uuid: event_id / session_key / result_id are all
  // `uuid` columns, so Postgres rejected it with 22P02 and - because the events insert is a single
  // upsert - one bad value discarded the whole batch of up to 50 events. Emit a real v4 instead.
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  function uuid() {
    try {
      if (crypto.randomUUID) return crypto.randomUUID();
      const b = crypto.getRandomValues(new Uint8Array(16));
      b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
      const h = Array.from(b, (n) => n.toString(16).padStart(2, "0"));
      return h.slice(0, 4).join("") + "-" + h.slice(4, 6).join("") + "-" + h.slice(6, 8).join("") +
             "-" + h.slice(8, 10).join("") + "-" + h.slice(10, 16).join("");
    } catch (_) {
      // No crypto at all: still emit a VALID v4 shape so the row is storable.
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
      });
    }
  }

  const S = {
    def: null, versions: null,
    answers: {}, history: [], current: null, trail: [],
    sessionKey: uuid(),
    dir: "fwd",
    warmed: false, result: null,
  };

  /* ---------- show_if evaluator (mirror of engine showif.ts; UX only, server re-checks) ---------- */
  function answerHas(ans, v) { return ans === undefined ? false : Array.isArray(ans) ? ans.includes(v) : ans === v; }
  function tracks() {
    const s = S.answers["G00_SCOPE"];
    return s === "both" ? ["face", "hair_scalp"] : s ? [s] : [];
  }
  function evalShowIf(c, shown, answers) {
    if (!c) return false;
    if (c.always) return true;
    if (c.track) return tracks().includes(c.track);
    if (c.any_track) return tracks().length > 0;
    if (c.shown) return shown.has(c.shown);
    if (c.any) return c.any.some((x) => evalShowIf(x, shown, answers));
    if (c.all) return c.all.every((x) => evalShowIf(x, shown, answers));
    if (c.not) return !evalShowIf(c.not, shown, answers);
    if (c.q) {
      const a = answers[c.q];
      if (c.eq !== undefined) return answerHas(a, c.eq);
      if (c.not_eq !== undefined) return a !== undefined && !answerHas(a, c.not_eq);
      if (c.in) return c.in.some((v) => answerHas(a, v));
      if (c.includes !== undefined) return answerHas(a, c.includes);
      if (c.includes_any) return c.includes_any.some((v) => answerHas(a, v));
      if (c.any_except) { if (a === undefined) return false; return (Array.isArray(a) ? a : [a]).some((v) => !c.any_except.includes(v)); }
    }
    return false;
  }
  // fixed point: stale answers of invisible questions never open branches (mirrors engine)
  function visibleIds() {
    let answers = { ...S.answers };
    let last = [];
    // each pass drops at least one answer when it has not settled, so the bound is the number of
    // questions, not a magic 10 (which 29 questions could outrun)
    for (let pass = 0; pass <= S.def.question_order.length; pass++) {
      const shown = new Set();
      for (const id of S.def.question_order) {
        const q = byId(id);
        if (q && evalShowIf(q.show_if, shown, answers)) shown.add(id);
      }
      last = [...shown];
      const kept = Object.keys(answers).filter((k) => shown.has(k));
      if (kept.length === Object.keys(answers).length) return last;
      answers = Object.fromEntries(kept.map((k) => [k, answers[k]]));
    }
    return last; // never [] : an empty list would silently end the quiz
  }
  function byId(id) { return S.def.questions.find((q) => q.id === id); }
  // A saved run may predate a question-set change: its answers can name questions or option
  // values that no longer exist. Keeping them poisons every later submit (the engine rejects the
  // payload and the stale state restores itself on the next reload, so the user is stuck). Drop
  // anything the CURRENT definition does not recognise; the user just re-answers that question.
  function pruneUnknownAnswers(answers) {
    const clean = {};
    for (const [qid, value] of Object.entries(answers || {})) {
      const q = byId(qid);
      if (!q) continue;
      const valid = new Set((q.options || []).map((o) => o.value));
      if (Array.isArray(value)) {
        const kept = value.filter((v) => valid.has(v));
        if (kept.length) clean[qid] = kept;
      } else if (valid.has(value)) {
        clean[qid] = value;
      }
    }
    return clean;
  }
  function nextUnanswered() {
    const vis = visibleIds();
    for (const id of vis) if (S.answers[id] === undefined) return id;
    return null;
  }

  /* ---------- api ---------- */
  const api = (p, opts) => fetch(cfg.apiBase + p, { headers: { "content-type": "application/json", "x-quiz-channel": cfg.channel || "live" }, ...opts });

  /* ---------- analytics (PRD 12.1): allowlisted props only, batched, idempotent ---------- */
  const evQueue = [];
  let evTimer = null;
  function track(event_name, props) {
    evQueue.push({
      event_id: uuid(),
      event_name, props: props || {},
      session_key: S.sessionKey, result_id: S.result?.result_id,
    });
    if (evQueue.length >= 8) flushEvents();
    else if (!evTimer) evTimer = setTimeout(flushEvents, 4000);
  }
  function flushEvents(useBeacon) {
    if (evTimer) { clearTimeout(evTimer); evTimer = null; }
    if (!evQueue.length) return;
    const batch = evQueue.splice(0, evQueue.length);
    const body = JSON.stringify(batch);
    // sendBeacon cannot reach this endpoint and never could: it forces credentials mode "include",
    // application/json is not CORS-safelisted so a preflight is required, and _shared/cors.ts does
    // not send Access-Control-Allow-Credentials - so the browser rejects the preflight and the POST
    // is never sent, while sendBeacon() still returns true (it reports queueing, not delivery).
    // Worse, the splice above has already emptied the queue, so a mid-quiz tab switch destroyed
    // those events permanently: the beacon branch was strictly worse than having no handler.
    // keepalive gives the same outlive-the-page guarantee over an ordinary CORS fetch, and keeps
    // the x-quiz-channel header a beacon could never send (without it preview traffic logs "live").
    api("/quiz-events", { method: "POST", body, keepalive: !!useBeacon })
      .catch(() => { /* analytics must never break the flow */ });
  }
  // window-level and therefore NOT torn down with the mount: register once, or the theme editor
  // stacks one listener per section re-render, each holding a stale config in its closure.
  if (!window.__ivaQuizVisWired) {
    window.__ivaQuizVisWired = true;
    addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flushEvents(true); });
    // visibilitychange is the only one that fires on a mobile app-switch, but WebKit does not fire
    // it reliably on same-tab navigation, where pagehide does. Both is safe: flushEvents returns
    // early on an empty queue, so the second call is a no-op and bfcache cannot duplicate events.
    addEventListener("pagehide", () => flushEvents(true));
  }
  function warm() { if (!S.warmed) { S.warmed = true; api("/quiz-recommendations").catch(() => {}); } }

  function fetchResult() {
    if (S.resultPromise) return S.resultPromise;
    const body = {
      quiz_version: S.versions.quiz_version, locale: S.versions.locale,
      question_set_version: S.versions.question_set_version, ruleset_version: S.versions.ruleset_version,
      answers: S.answers, session_key: S.sessionKey,
    };
    S.resultPromise = api("/quiz-recommendations", { method: "POST", body: JSON.stringify(body) })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw Object.assign(new Error(data.error || res.status), { data, status: res.status });
        return data;
      })
      .catch((e) => { S.resultPromise = null; throw e; });
    return S.resultPromise;
  }

  async function submit() {
    render(screenProcessing());
    // Hold the analysis screen for a minimum beat. The engine usually answers in about a second,
    // which flashes past and makes the result feel unconsidered. Configurable, 0 disables it.
    const minMs = Number((cfg.settings || {}).processing_min_ms ?? 4000);
    const heldUntil = Date.now() + minMs;
    const body = {
      quiz_version: S.versions.quiz_version, locale: S.versions.locale,
      question_set_version: S.versions.question_set_version, ruleset_version: S.versions.ruleset_version,
      answers: S.answers, session_key: S.sessionKey,
    };
    try {
      const data = await fetchResult();
      if (!data || !Array.isArray(data.profiles)) {
        throw new Error("Rezultatul nu a putut fi construit. Încearcă din nou.");
      }
      S.result = data;
      track("quiz_completed", {
        duration_ms: S.startedAt ? Date.now() - S.startedAt : undefined,
        resumed: S.resumed || undefined,
        visible_question_count: visibleIds().length,
        track_count: data.profiles.length,
      });
      track("quiz_result_viewed", {
        state: data.state, product_count: data.profiles.reduce((n, p) => n + p.routine.length, 0),
      });
      clearLocal(); // the run is complete; nothing left to resume
      flushEvents();
      const left = heldUntil - Date.now();
      if (left > 0) await new Promise((r) => setTimeout(r, left));
      render(screenResult(data));
    } catch (e) {
      if (e.status === 422 && e.data?.question_id) { S.resultPromise = null; goTo(e.data.question_id); return; }
      track("quiz_error", { code: String(e.message || e).slice(0, 40) });
      flushEvents();
      render(screenError(String(e.message || e)));
    }
  }

  /* ---------- flow ----------
   * Presentation order is the TRAIL (the screens actually shown), not question_order. The two
   * differ because eight questions are gated by answers given later in the catalog: F05/F07 by
   * G02, and F05/F09/F10 by F15/F16. Navigating by catalog position sent the user backwards onto
   * screens they had already answered, then made them press Continuă through every one of them to
   * get out. The trail only ever grows, so a late-unlocked question is simply the next screen.
   * question_order stays the authority for VISIBILITY (and for the server, which re-checks it). */
  function trailPrune() {
    const vis = new Set(visibleIds());
    S.trail = (S.trail || []).filter((id) => vis.has(id));
  }
  function start() { goTo(nextUnanswered() || S.def.question_order[0]); }
  function goTo(id) {
    S.current = id;
    S.dir = "fwd";
    trailPrune();
    if (!S.trail.includes(id)) S.trail.push(id);
    track("quiz_question_viewed", { question_id: id, position: S.trail.indexOf(id) + 1, total_visible: visibleIds().length });
    render(screenQuestion(byId(id)));
  }
  function answer(q, value, advance) {
    S.answers[q.id] = value;
    track("quiz_answered", { question_id: q.id, option_count: Array.isArray(value) ? value.length : 1 });
    if (q.id === "G00_SCOPE") track("quiz_scope_selected", { track_count: value === "both" ? 2 : 1 });
    warm();
    // stale-branch cleanup mirror: drop answers no longer visible
    const vis = new Set(visibleIds());
    for (const k of Object.keys(S.answers)) if (!vis.has(k)) delete S.answers[k];
    trailPrune(); // a pruned branch must leave the presentation trail too
    S.resultPromise = null; // answers changed: any pending computation is stale
    saveLocal();
    // Warm here, NOT only at render: answering can unlock or prune a branch, so the render-time
    // guess is made before we know where the user is going. By this line the answer is recorded
    // and visibleIds() reflects it, so peekNext() is accurate. Also warms the one after, which
    // covers a fast clicker who is already two screens ahead of the network.
    const n1 = peekNext();
    prefetchMedia(n1);
    if (n1) prefetchMedia(peekNext(n1));
    if (advance) next();
  }
  // Mirrors next()'s target selection WITHOUT navigating, so the frame we warm is the one the
  // user will actually land on. Deliberately adjacent to next(): if that selection changes, this
  // has to change with it or the prefetch silently starts guessing wrong again.
  function peekNext(from) {
    try {
      const vis = visibleIds();
      const cur = from === undefined ? S.current : from;
      return S.trail[S.trail.indexOf(cur) + 1]
        ?? vis.find((id) => S.answers[id] === undefined && !S.trail.includes(id))
        ?? null;
    } catch (_) { return null; }
  }
  function next() {
    trailPrune();
    const vis = visibleIds();
    // 1. walk the user's own history forward (they may have stepped Back to review)
    // 2. trail exhausted: append the first visible question not shown yet, wherever the catalog
    //    happens to put it - that is how a late-unlocked deep-dive arrives without a jump back
    const target = S.trail[S.trail.indexOf(S.current) + 1]
      ?? vis.find((id) => S.answers[id] === undefined && !S.trail.includes(id));
    if (target) { S.dir = "fwd"; goTo(target); return; }
    // safety net: a required answer went missing (a changed answer pruned a branch it fed)
    const missing = vis.find((id) => {
      const q = byId(id);
      return (q.required || q.required_when_shown) && S.answers[id] === undefined;
    });
    if (missing) { S.dir = "fwd"; goTo(missing); return; }
    // last question answered: offer to save the result, then reveal
    // PRD §4.2: the email step is optional and comes AFTER the result. "before" exists only as a
    // configurable variant and needs client sign-off (it also breaks the §2.4 KPI denominator).
    const emailEnabled = S.versions.email_capture_enabled || (cfg.settings || {}).preview_email_capture;
    const emailStep = emailEnabled && ((cfg.settings || {}).email_step || "after") === "before";
    if (emailStep && !S.emailDone) {
      S.dir = "fwd";
      fetchResult().catch(() => {}); // compute in the background so the reveal is instant
      render(screenEmail());
      return;
    }
    submit();
  }
  function back() {
    trailPrune();
    const i = S.trail.indexOf(S.current);
    S.dir = "back";
    if (i <= 0) { render(screenLanding()); return; } // answers are kept, not cleared
    S.current = S.trail[i - 1];
    render(screenQuestion(byId(S.current))); // walks the trail; does not re-append
  }

  /* ---------- rendering ---------- */
  function h(html) { const t = document.createElement("template"); t.innerHTML = html.trim(); return t.content; }
  function esc(s) { return String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c])); }
  // client metafield copy mixes lowercase/uppercase bullet starts; normalise to sentence case
  function sentence(t) { const x = String(t ?? "").trim(); return x ? x[0].toLocaleUpperCase("ro") + x.slice(1) : x; }
  // client copy marks species names with literal <i>/<em>; escape everything, then let ONLY
  // those two tags back through as real markup (any other tag stays visible text)
  function escI(t) { return esc(t).replace(/&lt;(\/?)(i|em)&gt;/gi, "<$1$2>"); }
  // Carousel transition: the current spread slides out while the next one slides in from the
  // opposite edge (forward: out left / in from right; Înapoi reverses). Both slides are in the
  // DOM only for the duration of the animation.
  const REDUCE_MOTION = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  function progressRail(pct) {
    let rail = document.getElementById("iva-quiz-progress");
    if (!rail) {
      rail = document.createElement("div");
      rail.id = "iva-quiz-progress";
      rail.className = "iva-quiz__rail";
      rail.setAttribute("role", "progressbar");
      rail.setAttribute("aria-label", "Progres evaluare");
      rail.innerHTML = "<i></i>";
      root.prepend(rail);
    }
    rail.hidden = pct === null;
    if (pct === null) return;
    rail.setAttribute("aria-valuemin", "0");
    rail.setAttribute("aria-valuemax", "100");
    rail.setAttribute("aria-valuenow", String(Math.round(pct)));
    rail.firstChild.style.width = pct + "%";
  }
  function progressFor(id) {
    const vis = visibleIds();
    const i = S.trail.indexOf(id);
    if (i < 0) return S.pctSeen || 0;
    S.totalSeen = Math.max(S.totalSeen || 0, vis.length, S.trail.length);
    const pct = ((i + 1) / (S.totalSeen + 1)) * 100;
    S.pctSeen = Math.max(S.pctSeen || 0, Math.min(96, pct)); // monotonic
    return S.pctSeen;
  }

  function render(frag) {
    const dir = S.dir || "fwd";
    S.dir = "fwd";
    const slide = document.createElement("div");
    slide.className = "iva-quiz__slide";
    slide.appendChild(frag);

    // every slide currently mounted is on its way out: a render fired mid-transition used to leave
    // the earlier one behind, stacking dead screens (with live Back/Continuă handlers) in the DOM
    const stale = [...root.querySelectorAll(".iva-quiz__slide")];
    const outgoing = stale[stale.length - 1];
    const isResult = !!slide.querySelector(".iva-quiz__result");
    if (!outgoing || REDUCE_MOTION || isResult) {
      root.replaceChildren(slide); // first paint, reduced motion, or the tall result page
    } else {
      root.classList.add("is-animating");
      outgoing.classList.add(dir === "fwd" ? "is-out-left" : "is-out-right");
      slide.classList.add(dir === "fwd" ? "is-in-right" : "is-in-left");
      root.appendChild(slide);
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        stale.forEach((s) => s.remove());
        root.classList.remove("is-animating");
        slide.classList.remove("is-in-right", "is-in-left");
      };
      slide.addEventListener("animationend", finish, { once: true });
      setTimeout(finish, 900); // safety net if the animation never fires
    }

    if (slide.querySelector(".iva-quiz__screen--intro")) progressRail(null);
    else if (slide.querySelector(".iva-quiz__result")) progressRail(100);
    else if (S.current) progressRail(progressFor(S.current));

    const focusEl = slide.querySelector("[data-focus]");
    // Tell the host which screen is on stage. The theme section uses this to show its header
    // only at the start and the end of the quiz, and to keep the footer out of the way while
    // the user is answering. Harmless in the lab, which has no theme chrome.
    try {
      const el = slide;
      document.body.dataset.ivaScreen =
        el.querySelector(".iva-quiz__result") ? "result"
        : (el.classList.contains("iva-quiz__screen--intro") || el.querySelector(".iva-quiz__screen--intro")) ? "intro"
        : (el.classList.contains("iva-quiz__screen--processing") || el.querySelector(".iva-quiz__screen--processing")) ? "processing"
        : el.querySelector("[data-qid]") ? "question"
        : "other";
    } catch { /* never let chrome bookkeeping break a render */ }
    if (focusEl) { focusEl.setAttribute("tabindex", "-1"); focusEl.focus({ preventScroll: true }); }
    // The lab document was exactly one viewport tall, so screens were always perfectly framed.
    // A real Shopify page has the theme footer below the quiz and therefore scrolls: without
    // this, a user who scrolled during one question lands mid-way down the next one.
    const mountBox = root.getBoundingClientRect();
    if (mountBox.top < -4 || mountBox.top > 4) {
      window.scrollTo({ top: window.scrollY + mountBox.top, behavior: REDUCE_MOTION ? "auto" : "smooth" });
    }
  }
  // Asset contract (client Decision 14, 11 Aug 2026): every visual carries id, alt text,
  // mobile variant, fallback and an active flag. An inactive/missing/failed asset degrades to
  // the neutral placeholder - screens never depend on imagery being present.
  // Past the entry point a phone gets type on cream, no photo (Typology's own mobile pattern):
  // the image ate the fold and pushed the answers under it. The CSS hides it too, but hidden
  // images are still fetched, so on a phone we simply never emit one.
  const PHONE_MEDIA = new Set(["LANDING"]);
  const isPhone = () => matchMedia("(max-width: 599px)").matches;

  // Warm the next screen's frame while the user is still reading the current question, so the
  // panel does not go blank on Continue. One request that was going to happen anyway, moved
  // earlier. Cheap: the browser dedupes against the same URL when the <img> is finally rendered.
  const warmed = new Set();
  function prefetchMedia(qid) {
    if (!qid || warmed.has(qid) || isPhone()) return;
    const m = (cfg.media || {})[qid];
    if (!m || m.active === false || !m.img || m.video) return;
    warmed.add(qid);
    const im = new Image();
    if (m.srcset) { im.srcset = m.srcset; im.sizes = "50vw"; }
    im.src = m.img;
  }

  function media(qid, cls) {
    if (isPhone() && !PHONE_MEDIA.has(qid)) return "";
    const m = (cfg.media || {})[qid];
    const usable = m && m.active !== false && (m.img || m.video);
    let inner = "";
    if (usable && m.video) {
      // muted+inline+loop so it autoplays on mobile; poster carries the first frame for LCP
      inner = `<video src="${esc(m.video)}"${m.img ? ` poster="${esc(m.img)}"` : ""} autoplay muted loop playsinline preload="metadata" aria-hidden="true"></video>`;
    } else if (usable) {
      const src = esc(m.img);
      const mobile = m.mobile ? `<source media="(max-width: 899px)" srcset="${esc(m.mobile)}">` : "";
      const fallback = m.fallback ? ` onerror="this.onerror=null;this.src='${esc(m.fallback)}'"` : ` onerror="this.remove()"`;
      // srcset + sizes: the panel is 50vw and is hidden entirely below 900px, so a standard
      // non-retina desktop pulls the 900w rendition (~27KB) instead of the full 1600w (~85KB).
      // Shopify's CDN also negotiates WebP on theme assets, so this compounds.
      const ss = m.srcset ? ` srcset="${esc(m.srcset)}" sizes="50vw"` : "";
      // intrinsic ratio, so the panel does not reflow as each frame arrives
      const dim = m.w && m.h ? ` width="${m.w}" height="${m.h}"` : "";
      inner = `<picture>${mobile}<img src="${src}"${ss}${dim} alt="${esc(m.alt || "")}" loading="eager" decoding="async"${fallback}></picture>`;
    }
    return `<div class="iva-quiz__media ${cls || ""}" data-asset="${esc(qid)}">` +
      `<div class="iva-quiz__media-placeholder" aria-hidden="true">${esc((cfg.settings || {}).media_placeholder || "Ivatherm")}</div>${inner}</div>`;
  }

  // Brand mark: logo when the host supplies one (theme: asset_url; lab: local file), else wordmark.
  function brandMark() {
    const src = (cfg.settings || {}).logo_url;
    return src
      ? `<img class="iva-quiz__logo" src="${esc(src)}" alt="Ivatherm">`
      : `<p class="iva-quiz__kicker">Ivatherm</p>`;
  }

  // INTRO (PRD §4.2): promise, time estimate, short disclaimer, one CTA. The first question
  // (G00) comes after the CTA as a normal screen - no question is embedded here.
  function screenLanding() {
    const st = cfg.settings || {};
    const frag = h(`
    <div class="iva-quiz__screen iva-quiz__screen--intro">
      ${media("LANDING")}
      <div class="iva-quiz__panel"><div class="iva-quiz__card iva-quiz__card--intro">
        ${brandMark()}
        <p class="iva-quiz__kicker">${esc(st.kicker || "Evaluare dermatocosmetică")}</p>
        <h1 class="iva-quiz__title" data-focus>${esc(st.headline || "Rutina ta, în 2 minute.")}</h1>
        <p class="iva-quiz__disclaimer">${esc(st.subline || "Răspunde simplu, în limbajul tău. Primești o rutină explicată, construită pentru nevoile tale.")}</p>
        <button type="button" class="iva-quiz__btn iva-quiz__btn--primary iva-quiz__btn--start" data-start>${esc(st.cta || "Începe evaluarea")}</button>
        <p class="iva-quiz__legal">${esc(st.disclaimer || "Recomandare dermatocosmetică. Nu punem diagnostice și nu înlocuim medicul dermatolog.")}</p>
      </div></div>
    </div>
    ${statsRow(st)}`);
    frag.querySelector("[data-start]").addEventListener("click", () => {
      warm(); // isolate + preflight hot while the user answers
      S.startedAt = Date.now();
      // Every genuinely fresh run passes through this button, so clearing the flag here covers
      // both routes back to the landing screen: [data-restart] (which resets seven other pieces
      // of state but not this one) and back() past question 1. Without it, a resumed run followed
      // by a fresh one reports the fresh run as resumed, mislabelling exactly the rows the flag
      // exists to tell apart.
      S.resumed = false;
      track("quiz_started", {
        quiz_version: S.versions.quiz_version, locale: S.versions.locale,
        source_campaign: entryPoint || "direct", // dead cfg fallbacks removed: neither ever existed
      });
      // resuming a run that is already complete must not dump the user back on question 1
      const resume = nextUnanswered() ?? S.trail[S.trail.length - 1] ?? S.def.question_order[0];
      goTo(resume);
    });
    return frag;
  }
  // Trust row: OFF unless the host explicitly supplies stats. Any figure here is a marketing
  // claim on a medical-adjacent page and needs client + medical approval before it ships.
  function statsRow(st) {
    if (!Array.isArray(st.stats) || st.stats.length === 0) return "";
    return `<div class="iva-quiz__stats">${st.stats.map(([b, s]) => `<div class="iva-quiz__stat"><b>${esc(b)}</b><span>${esc(s)}</span></div>`).join("")}</div>`;
  }

  function screenQuestion(q) {
    const vis = visibleIds();
    const pos = (S.trail.indexOf(q.id) + 1) || vis.indexOf(q.id) + 1; // shown-order, not catalog order
    // PRD 14.3: the denominator only ever grows, so the bar never slides backwards when a
    // branch opens; it is the honest "questions we know about so far".
    S.totalSeen = Math.max(S.totalSeen || 0, vis.length, S.trail.length);
    const total = S.totalSeen;
    const isMulti = q.type === "multi";
    const stored = S.answers[q.id];
    const picked = new Set(Array.isArray(stored) ? stored : []);
    // concern loop: hide options already chosen in earlier rounds (ui.filter_already_selected)
    // Hide concerns already taken in an earlier round. Fully data-driven: an option is a
    // duplicate when its `secondary` effect equals a concern already chosen (as primary or
    // secondary) in another round of the loop - no hardcoded question/value map.
    const hide = new Set();
    if (q.ui && q.ui.filter_already_selected) {
      const rounds = [...(S.def.concern_loop?.rounds ?? []), ...(q.ui.filter_from ?? [])];
      const taken = new Set();
      for (const rid of rounds) {
        if (rid === q.id) continue;
        const ans = S.answers[rid];
        if (typeof ans !== "string") continue;
        const opt = byId(rid)?.options.find((o) => o.value === ans);
        if (opt?.effects?.primary) taken.add(opt.effects.primary);
        if (opt?.effects?.secondary) taken.add(opt.effects.secondary);
        taken.add(ans);
      }
      for (const o of q.options) {
        if (taken.has(o.value) || (o.effects?.secondary && taken.has(o.effects.secondary))) hide.add(o.value);
      }
    }
    const opts = q.options.filter((o) =>
      !hide.has(o.value) && (!o.show_if || evalShowIf(o.show_if, new Set(), S.answers)));
    const qText = ((q.copy_variants || []).find((v) => evalShowIf(v.when, new Set(), S.answers)) || q.copy).question;
    const frag = h(`
    <div class="iva-quiz__screen" data-qid="${esc(q.id)}">
      ${media(q.id)}
      <div class="iva-quiz__panel"><div class="iva-quiz__card">
        ${brandMark()}
        <h2 class="iva-quiz__title" data-focus>${esc(qText)}</h2>
        ${q.copy.helper ? `<p class="iva-quiz__helper">${esc(q.copy.helper)}</p>` : ""}
        ${isMulti && q.max_select ? `<p class="iva-quiz__helper">Alege maximum ${q.max_select}.</p>` : ""}
        <div role="${isMulti ? "group" : "radiogroup"}" aria-label="${esc(qText)}">
          <ul class="iva-quiz__options">${opts.map((o) => `
            <li><button type="button" class="iva-quiz__option ${isMulti ? "" : "iva-quiz__option--single"}"
              role="${isMulti ? "checkbox" : "radio"}" aria-checked="${isMulti ? picked.has(o.value) : stored === o.value}" data-opt="${esc(o.value)}">
              <span class="iva-quiz__option-mark"></span>${esc(o.label)}</button></li>`).join("")}
          </ul>
        </div>
        <p class="iva-quiz__limit" aria-live="polite" data-limit></p>
        <div class="iva-quiz__nav">
          <button type="button" class="iva-quiz__btn iva-quiz__btn--ghost" data-back>Înapoi</button>
          <button type="button" class="iva-quiz__btn iva-quiz__btn--primary" data-next${answered(q) ? "" : " disabled"}>Continuă</button>
        </div>
        <p class="iva-quiz__progress-label">Întrebarea ${pos} din ${total}</p>
        <button type="button" class="iva-quiz__restart" data-restart>Ia evaluarea de la început</button>
      </div></div>
    </div>`);
    wireQuestion(frag, q, picked);
    return frag;
  }

  function answered(q) {
    const a = S.answers[q.id];
    return Array.isArray(a) ? a.length > 0 : a !== undefined;
  }

  function wireQuestion(frag, q, picked) {
    // Warm the frame the user is most likely to see next. Uses plain visible-order rather than
    // next()'s trail walk: a branch can still divert them, and being wrong costs one small
    // request. PROCESS is warmed on the last question, since that screen appears instantly.
    try {
      const n1 = peekNext(q.id);
      prefetchMedia(n1 || "PROCESS");
      if (n1) prefetchMedia(peekNext(n1));
    } catch (_) { /* prefetch is an optimisation; never let it break the question */ }
    const isMulti = q.type === "multi";
    const nextBtn = frag.querySelector("[data-next]");
    const limitEl = frag.querySelector("[data-limit]");
    frag.querySelector("[data-back]").addEventListener("click", back);
    frag.querySelector("[data-restart]")?.addEventListener("click", () => {
      if (!confirm("Reiei evaluarea de la început? Răspunsurile date până acum se șterg.")) return;
      // This restart jumps straight to question 1 and never renders the landing screen, so the
      // reset on [data-start] does NOT cover it. Without these two lines a resumed run restarted
      // from here reports resumed:true with a duration inflated by the abandoned pre-restart span.
      S.resumed = false; S.startedAt = Date.now();
      S.answers = {}; S.trail = []; S.totalSeen = 0; saveLocal();
      render(screenQuestion(byId(S.def.question_order[0])));
    });
    frag.querySelectorAll(".iva-quiz__option").forEach((btn) => {
      btn.addEventListener("click", () => {
        const v = btn.dataset.opt;
        if (!isMulti) {
          // (a frag.querySelectorAll here would be a no-op: the fragment is empty by now)
          root.querySelectorAll(".iva-quiz__option").forEach((b) => b.setAttribute("aria-checked", String(b.dataset.opt === v)));
          // No auto-advance (client, 21 Aug): several users asked to control "Continuă"
          // themselves - tap, read, change their mind, then move on. The answer is stored
          // immediately; only the advance waits for the button.
          answer(q, v, false);
          if (nextBtn) nextBtn.disabled = false;
          return;
        }
        const exclusive = (q.exclusive_values || []);
        if (exclusive.includes(v)) { picked.clear(); picked.add(v); }
        else {
          exclusive.forEach((x) => picked.delete(x));
          if (picked.has(v)) picked.delete(v);
          else if (!q.max_select || picked.size < q.max_select) picked.add(v);
          else { limitEl.textContent = `Poți alege maximum ${q.max_select} opțiuni.`; return; }
        }
        limitEl.textContent = "";
        root.querySelectorAll(".iva-quiz__option").forEach((b) => b.setAttribute("aria-checked", String(picked.has(b.dataset.opt))));
        nextBtn.disabled = picked.size === 0;
      });
    });
    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        if (isMulti) answer(q, [...picked], true);
        else next(); // single already stored; Next just moves on
      });
    }
  }

  // Full bleed, no split: the one screen that breaks the half-and-half rhythm, so the analysis
  // reads as a moment of work rather than another form step. Held for a minimum time (below).
  function screenProcessing() {
    const st = cfg.settings || {};
    const m = (cfg.media || {}).PROCESS;
    const usable = m && m.active !== false && m.img;
    return h(`<div class="iva-quiz__screen iva-quiz__screen--processing">
      ${usable ? `<img class="iva-quiz__process-bg" src="${esc(m.img)}" alt="" aria-hidden="true">` : ""}
      <div class="iva-quiz__process-inner">
        ${brandMark()}
        <h2 class="iva-quiz__title" data-focus>${esc(st.processing_title || "Îți construim rutina.")}</h2>
        <p class="iva-quiz__helper">${esc(st.processing_sub || "Analizăm răspunsurile tale și verificăm regulile de siguranță.")}</p>
        <span class="iva-quiz__spinner" aria-hidden="true"></span>
        <p class="iva-quiz__sr" role="status">Se calculează rezultatul.</p>
      </div></div>`);
  }

  const STATE_COPY = {
    support_only: ["Urmezi un tratament prescris", "Rutina de mai jos este de suport și nu înlocuiește tratamentul. Verifică împreună cu medicul înainte de a adăuga produse noi."],
    minimal_tolerance: ["Rutină minimală, cu toleranță ridicată", "Am păstrat doar esențialul, pentru că pielea ta reacționează ușor."],
    consult_first: ["Recomandăm un consult dermatologic", "Răspunsurile tale indică o situație care merită evaluată de un medic. Mai jos îți arătăm doar îngrijire blândă de suport, fără produse active."],
    // Regulatory, not clinical: a cosmetics company may not recommend anything during pregnancy or
    // breastfeeding. Rendered INSTEAD of consult_first when the engine flags it, because that copy
    // points at support products which deliberately do not exist here.
    regulatory_pregnancy: ["Recomandăm un consult medical", "În sarcină și în perioada de alăptare nu îți putem recomanda produse cosmetice. Medicul tău îți poate spune ce este potrivit în această perioadă."],
    partial_stock: ["Un produs nu este momentan disponibil", "Am compus rutina din produsele disponibile; pasul lipsă revine în stoc curând."],
    no_match: ["Nu am găsit o potrivire sigură", "Preferăm să nu recomandăm la întâmplare. Reia evaluarea sau scrie-ne."],
  };
  // Advisory safety messages (engine emits these in profile.safety_messages). They are a SEPARATE
  // tier from the state block above: a zone can be clinically "standard" and still carry an
  // advisory, e.g. 6+ months of patterned hair loss. Until this existed the engine computed the
  // whole tier and the page threw it away (review finding 1.2).
  // support_only and consult_first are deliberately absent: STATE_COPY already renders those, and
  // repeating them would say the same thing twice.
  const ADVISORY_RO = {
    // Regulatory Affairs made this the condition of recommending anything at all during pregnancy
    // or breastfeeding, so it is not optional decoration: retinoids and exfoliating acids are
    // already excluded from the routine, and this states the rest.
    dermatologist_recommended: "Îți recomandăm și o evaluare la medicul dermatolog pentru situația pe care ai descris-o.",
    aga_adjunct_only: "Produsele pentru păr sunt îngrijire adjuvantă. Ele nu înlocuiesc un tratament recomandat de medic.",
    medical_note: "Verifică împreună cu medicul înainte de a adăuga produse noi.",
  };
  // fail closed: an unmapped advisory still reaches the user rather than disappearing
  const ADVISORY_FALLBACK = "Discută această situație cu medicul dermatolog.";

  const DIM_RO = { hydration: "Hidratare", comfort: "Confort", barrier: "Barieră", uniformity: "Uniformitate", sebum: "Sebum", firmness: "Fermitate", density: "Densitate", fiber: "Rezistența firului", scalp_balance: "Echilibrul scalpului" };
  const BAND_RO = { balanced: "echilibrat", mild: "nevoie ușoară", moderate: "nevoie moderată", priority: "prioritar", not_assessed: "neevaluat" };
  const CONCERN_RO = {
    dehydration: "deshidratare", hydration: "hidratare", dryness: "uscăciune", sensitivity: "sensibilitate",
    intolerance: "toleranță scăzută", acne_sebum: "imperfecțiuni", post_acne_marks: "pete post-acneice",
    pigmentation: "pete pigmentare", redness: "roșeață", seb_scaling: "scuame", aging: "riduri",
    firmness: "fermitate", eye: "zona ochilor", radiance: "luminozitate", photoprotection: "protecție solară",
    hair_loss: "cădere", density: "densitate", fiber_fragility: "fir fragil", scalp_balance: "echilibrul scalpului",
    scalp_comfort: "confortul scalpului", scalp_sebum: "scalp gras", rosacea: "rozacee",
    seborrheic_dermatitis: "dermatită seboreică", atopic_dermatitis: "dermatită atopică",
    androgenetic_alopecia: "alopecie androgenetică", telogen_effluvium: "efluviu telogen", acne: "acnee",
    light: "textură lejeră", rich: "textură bogată", mat: "finisaj mat",
    sebum_control: "exces de sebum",
  };
  const TITLE_RO = {
    face: { dryness: "Ten uscat", dehydration: "Ten deshidratat", acne_sebum: "Ten gras cu imperfecțiuni",
      pigmentation: "Ten cu pete pigmentare", aging: "Ten matur", firmness: "Ten cu fermitate redusă",
      sensitivity: "Ten sensibil", intolerance: "Ten intolerant", redness: "Ten cu roșeață",
      sebum_control: "Ten gras, fără imperfecțiuni",
      seb_scaling: "Ten cu scuame și roșeață", repair: "Ten care are nevoie de reparare",
      eye: "Zona ochilor", radiance: "Ten fără luminozitate", undetermined: "Tenul tău" },
    hair_scalp: { hair_loss: "Cădere a părului", density: "Densitate redusă", fiber_fragility: "Fir fragil",
      scalp_balance: "Scalp cu mătreață", scalp_comfort: "Scalp sensibil", scalp_sebum: "Scalp gras",
      undetermined: "Părul și scalpul tău" },
  };
  const SECONDARY_RO = {
    dehydration: "tendință la deshidratare", dryness: "uscăciune", sensitivity: "sensibilitate",
    acne_sebum: "imperfecțiuni", post_acne_marks: "pete post-acneice", pigmentation: "pete pigmentare",
    redness: "roșeață", seb_scaling: "scuame", aging: "primele riduri", firmness: "fermitate redusă",
    eye: "nevoi în zona ochilor", radiance: "aspect tern", scalp_balance: "mătreață",
    scalp_comfort: "disconfort al scalpului", scalp_sebum: "exces de sebum", density: "densitate redusă",
    fiber_fragility: "fir fragil", hair_loss: "cădere", sebum_control: "exces de sebum",
  };
  const ADJUNCT_RO = { acne: "acnee", androgenetic_alopecia: "alopecie androgenetică", telogen_effluvium: "efluviu telogen" };
  const OBSERVATION_RO = {
    "dimension:hydration": "Ai semnalat nevoie de hidratare.",
    "dimension:comfort": "Pielea ta reacționează ușor și are nevoie de confort.",
    "dimension:barrier": "Bariera cutanată pare fragilizată.",
    "dimension:uniformity": "Ai semnalat neuniformități ale tenului.",
    "dimension:sebum": "Ai semnalat exces de sebum și imperfecțiuni.",
    "dimension:firmness": "Ai semnalat semne de îmbătrânire sau fermitate redusă.",
    "dimension:density": "Ai semnalat pierdere de densitate.",
    "dimension:fiber": "Ai semnalat un fir fragil, care se rupe.",
    "dimension:scalp_balance": "Ai semnalat dezechilibre la nivelul scalpului.",
    "flag:support_only": "Ai spus că urmezi un tratament prescris, așa că rutina îl completează.",
    "flag:minimal_routine": "Ai spus că pielea ta nu suportă aproape nimic, așa că am păstrat minimul.",
  };
  const OBJECTIVE_RO = {
    "slot:cleanse": "curățare blândă, fără să usuce",
    "slot:moisturize": "hidratare și confort pe parcursul zilei",
    "slot:spf": "protecție solară zilnică",
    "slot:treatment": "un singur produs de tratament, fără suprapuneri",
    "concern:dryness": "reducerea senzației de uscăciune",
    "concern:dehydration": "refacerea nivelului de hidratare",
    "concern:acne_sebum": "reducerea imperfecțiunilor și controlul sebumului",
    "concern:pigmentation": "uniformizarea tonului",
    "concern:aging": "atenuarea primelor riduri",
    "concern:firmness": "susținerea fermității",
    "concern:sensitivity": "calmarea sensibilității",
    "concern:intolerance": "revenirea la o rutină tolerabilă",
    "concern:redness": "reducerea aspectului de roșeață",
    "concern:seb_scaling": "reducerea scuamelor",
    "concern:repair": "susținerea refacerii pielii",
    "concern:hair_loss": "susținerea densității părului",
    "concern:density": "susținerea densității părului",
    "concern:fiber_fragility": "întărirea firului",
    "concern:scalp_balance": "echilibrarea scalpului",
    "concern:scalp_comfort": "calmarea scalpului",
    "concern:scalp_sebum": "controlul sebumului la nivelul scalpului",
  };
  const WHEN_RO = { am: "dimineața", pm: "seara", both: "dimineața și seara" };

  const NARRATIVE_RO = {
    dryness: "Pielea uscată produce mai puține lipide, așa că bariera reține apa mai greu și apare senzația de piele care ține. O rutină simplă, cu curățare blândă și un strat bogat de hidratare, face de obicei cea mai mare diferență.",
    dehydration: "Deshidratarea înseamnă lipsă de apă, nu de sebum: pielea poate fi în același timp lucioasă și inconfortabilă. Se lucrează cu texturi lejere, aplicate constant, dimineața și seara.",
    acne_sebum: "Când sebumul este în exces, porii se încarcă mai repede și apar imperfecțiuni. Rutina ține echilibrul fără să usuce, pentru că pielea deshidratată produce și mai mult sebum.",
    pigmentation: "Petele și neuniformitățile se accentuează la soare, așa că protecția solară zilnică contează la fel de mult ca produsul de uniformizare.",
    aging: "Se lucrează cu hidratare constantă, un activ tolerat și protecție solară.",
    sebum_control: "Excesul de sebum se echilibrează, nu se elimină: curățare potrivită, o cremă care reglează sebumul și protecție solară care nu încarcă.",
    firmness: "Fermitatea ține de densitatea și elasticitatea pielii. Rutina susține confortul zilnic și textura, fără promisiuni de lifting.",
    sensitivity: "Pielea sensibilă reacționează repede la produse noi și la factori externi. Cu cât rutina are mai puține variabile, cu atât e mai ușor de urmărit ce îi priește.",
    intolerance: "Când pielea nu suportă aproape nimic, prioritatea este să revină la o bază tolerabilă: puține produse, fără active, aplicate constant câteva săptămâni.",
    redness: "Roșeața persistentă cere produse care calmează și evită factorii care o accentuează. Nu tratăm cauza, ci confortul zilnic al pielii.",
    seb_scaling: "Scuamele și roșeața din zonele seboreice apar în episoade. Rutina urmărește echilibrul, cu o fază mai intensă și una de întreținere.",
    repair: "Pielea fragilizată are nevoie de o pauză de la active și de produse care susțin refacerea barierei.",
    hair_loss: "Densitatea părului variază în timp și depinde de mai mulți factori. Rutina susține scalpul și firul, fără să promită oprirea căderii.",
    density: "Un păr care pare mai rar cere întâi un scalp echilibrat: acolo începe rutina.",
    fiber_fragility: "Firul fragil se rupe pe lungime, nu cade din rădăcină. Se lucrează pe rezistența firului și pe blândețea rutinei.",
    scalp_balance: "Scalpul are propriul echilibru: descuamare, sebum și confort. Rutina alternează o fază intensă cu una de întreținere.",
    scalp_comfort: "Un scalp sensibil are nevoie de produse blânde și de pauze între active.",
    scalp_sebum: "Scalpul care se îngrașă repede cere curățare regulată, dar nu agresivă, altfel producția de sebum crește.",
  };
  function narrative(r, main) {
    const key = main?.title?.primary;
    return NARRATIVE_RO[key] || "Profilul tău este construit din răspunsurile pe care le-ai dat, nu dintr-un diagnostic. Rutina de mai jos urmărește nevoile pe care le-ai semnalat.";
  }

  function profileTitle(p) {
    const base = (TITLE_RO[p.track] || {})[p.title?.primary] || TITLE_RO[p.track].undetermined;
    const sec = p.title?.secondary && SECONDARY_RO[p.title.secondary];
    const title = sec ? `${base} cu ${sec}` : base;
    // PRD 10.2: a guarded condition may only appear when the user declared it, and then prefixed
    return p.title?.adjunct_for
      ? `Îngrijire adjuvantă pentru ${ADJUNCT_RO[p.title.adjunct_for]}: ${title.toLowerCase()}`
      : title;
  }

  const SLOT_RO = { cleanse: "Curățare", treatment: "Tratament", moisturize: "Hidratare", spf: "Protecție solară", eye: "Contur ochi", scalp_serum: "Ser scalp", shampoo_maintenance: "Șampon întreținere", shampoo_intensive: "Șampon intensiv", mist: "Apă termală" };
  // The client objected to reading "Tratament: Ivawhite Ser" - the product is a serum, and the
  // slot is our internal word, not theirs (19 Aug). When the catalogue carries the product's own
  // type we print that; the slot stays the fallback.
  const TYPE_RO = { ser: "Ser", fluid: "Fluid", crema: "Cremă", "cremă": "Cremă", ulei: "Ulei",
    spuma: "Spumă", gel: "Gel", sampon: "Șampon", "șampon": "Șampon", apa: "Apă termală", masca: "Mască" };
  const roleOf = (s) => (s.product_type && (TYPE_RO[s.product_type] || s.product_type)) || SLOT_RO[s.slot] || s.slot;

  function screenResult(r) {
    const face = r.profiles.find((p) => p.track === "face");
    const hair = r.profiles.find((p) => p.track === "hair_scalp");
    const main = face || hair;
    // Two axes per zone (engine 0.3.0 / client Decision 5): clinical state and availability state
    // are independent, and each zone reports its own - a consult_first scalp never silences the
    // valid face routine.
    const zoneLabel = (t) => (t === "face" ? "Ten" : "Păr & scalp");
    // Two tiers, two placements (Julian, 17 Aug): this is an e-commerce page, so the routine sits
    // directly under the hero. STATE blocks (consult/support/minimal/stock) stay at the top of the
    // product column because PRD 11 requires the medical message before the commercial CTA. The
    // softer ADVISORY notes move down into the diagnostic section instead of opening the page.
    const many = r.profiles.length > 1;
    // one message per zone: the clinical reason wins; availability only speaks when the
    // clinical state is fine (otherwise "no match" just restates the consult message).
    // When BOTH zones share the same state, say it ONCE ("Ten și păr & scalp: ...") - the
    // client's screenshot showed the same sentence stacked per zone.
    const stateZones = new Map(); // state key -> tracks
    for (const p of r.profiles) {
      const key = p.clinical_state !== "standard" ? p.clinical_state
        : p.availability_state !== "complete" ? p.availability_state : null;
      if (!key || !STATE_COPY[key]) continue;
      if (!stateZones.has(key)) stateZones.set(key, []);
      stateZones.get(key).push(p.track);
    }
    const safetyStates = [...stateZones.entries()].map(([key, zones]) => {
      const copy = STATE_COPY[key];
      const prefix = !many ? "" : zones.length === r.profiles.length
        ? "Ten și păr & scalp: " : zoneLabel(zones[0]) + ": ";
      return `<div class="iva-quiz__safety ${key === "consult_first" ? "iva-quiz__safety--consult" : ""}">
          <h4>${esc(prefix + copy[0])}</h4><p>${esc(copy[1])}</p></div>`;
    }).join("");
    // Client screenshot, 21 Aug: "toate casetele spun același lucru" - five near-identical
    // advisory boxes. Dedupe ACROSS zones (one note shown once, unprefixed when both zones share
    // it) and collapse the generic "verifică cu medicul" when the stronger "evaluare la
    // dermatolog" is already present for the same zone: both just say "see a doctor".
    const noteZones = new Map(); // note text -> Set of tracks
    for (const p of r.profiles) {
      let keys = (p.safety_messages || [])
        .filter((m) => !m.startsWith("consult_first") && m !== "support_only");
      if (keys.includes("dermatologist_recommended")) keys = keys.filter((m) => m !== "medical_note");
      for (const note of new Set(keys.map((m) => ADVISORY_RO[m] || ADVISORY_FALLBACK))) {
        if (!noteZones.has(note)) noteZones.set(note, new Set());
        noteZones.get(note).add(p.track);
      }
    }
    const advisoryNotes = [...noteZones.entries()].map(([note, zones]) => {
      const prefix = many && zones.size < r.profiles.length
        ? zoneLabel([...zones][0]) + ": " : "";
      return `<div class="iva-quiz__safety iva-quiz__safety--note">
            <p>${esc(prefix + note)}</p></div>`;
    }).join("");

    // PRD 9.3: the same product used morning and evening is shown once, with the difference explained
    // application order within a daypart mirrors the engine's protocol (cleanse first, SPF last).
    // Rendering full cards before repeat rows put the evening acid ABOVE the cleanser (UI-01).
    const SLOT_ORDER = ["cleanse", "mist", "treatment", "scalp_serum", "shampoo_intensive", "shampoo_maintenance", "eye", "moisturize", "mask", "spf"]; // mist after cleanse (client, 21 Aug confirmations)
    const slotRank = (s) => { const i = SLOT_ORDER.indexOf(s.slot); return i === -1 ? SLOT_ORDER.length : i; };
    const daypart = (steps, label, key, sellable) => {
      const own = steps.filter((s) => s[key] && !(s.am && s.pm && key === "pm"));
      const shared = key === "pm" ? steps.filter((s) => s.am && s.pm) : [];
      if (!own.length && !shared.length) return "";
      const rows = [
        ...own.map((s) => ({ s, kind: "full" })),
        ...shared.map((s) => ({ s, kind: "repeat" })),
      ].sort((a, b) => slotRank(a.s) - slotRank(b.s));
      const repeatRow = (rs) => `
          <article class="iva-quiz__product iva-quiz__product--repeat">
            <div class="iva-quiz__product-img">${rs.image ? `<img src="${esc(rs.image)}" alt="" loading="lazy">` : ""}</div>
            <div>
              <h3>${esc(rs.product_name)}</h3>
              <p class="iva-quiz__product-meta"><span>${esc(roleOf(rs))}</span><span>același produs ca dimineața</span></p>
            </div>
            <span class="iva-quiz__repeat-mark">↺</span>
          </article>`;
      return `<section class="iva-quiz__daypart"><h2>${label}</h2>
        ${rows.map((r0) => r0.kind === "full" ? productCard(r0.s, sellable) : repeatRow(r0.s)).join("")}
      </section>`;
    };
    const money = (v, c) => (v == null ? "" : `${Number(v).toFixed(2).replace(".", ",")} ${c || "RON"}`);
    const FREQ_RO = { twice_weekly: "de 2 ori pe săptămână", "3x_weekly": "de 3 ori pe săptămână",
      weekly: "o dată pe săptămână", alternate_days: "o zi da, una nu", unspecified: "vezi modul de folosire" };
    const productCard = (s, sellable = true) => `
      <article class="iva-quiz__product" data-sku="${esc(s.sku_id)}">
        <button type="button" class="iva-quiz__product-img" data-quick="${esc(s.sku_id)}" aria-label="Vezi ${esc(s.product_name)}">${s.image
          ? `<img src="${esc(s.image)}" srcset="${esc(s.image)} 1x${s.image_2x ? `, ${esc(s.image_2x)} 2x` : ""}" alt="${esc(s.image_alt || s.product_name)}" loading="lazy" decoding="async">`
          : "foto"}</button>
        <div>
          <h3>${esc(s.product_name)}</h3>
          ${s.actives ? `<p class="iva-quiz__product-actives">${esc(s.actives)}</p>` : ""}
          <p class="iva-quiz__product-meta">
            ${s.rating ? `<span class="iva-quiz__stars">★ ${esc(Number(s.rating).toFixed(1))}/5</span>` : ""}
            <span>${esc(roleOf(s))}</span>
            ${s.price ? `<b>${esc(money(s.price, s.currency))}</b>` : ""}
            ${s.compare_at && Number(s.compare_at) > Number(s.price) ? `<s>${esc(money(s.compare_at, s.currency))}</s>` : ""}
          </p>
          ${s.matched_answer_ids.slice(0, 1).map((m) => `<span class="iva-quiz__chip">pentru: ${esc(CONCERN_RO[m.split(":")[1]] || m.split(":")[1] || m)}</span>`).join("")}
          <p class="iva-quiz__why-product">${esc(explainStep(s))}</p>
          ${s.frequency && s.frequency !== "daily" ? `<span class="iva-quiz__chip iva-quiz__chip--freq">${esc(FREQ_RO[s.frequency] || "nu zilnic, vezi modul de folosire")}</span>` : ""}
          ${s.available === false ? `<span class="iva-quiz__chip">temporar indisponibil</span>` : ""}
          ${stepPanels(s)}
          <button type="button" class="iva-quiz__more" data-quick="${esc(s.sku_id)}">Vezi mai mult</button>
        </div>
        ${sellable ? `<button type="button" class="iva-quiz__toggle" data-toggle="${esc(s.sku_id)}" aria-pressed="true">
          <span class="iva-quiz__toggle-on">În rutină ✓</span><span class="iva-quiz__toggle-off">Adaugă înapoi</span>
        </button>` : `<span class="iva-quiz__chip iva-quiz__chip--support">suport blând, nu face parte din comandă</span>`}
      </article>`;

    // Expandable detail per product, Typology's pattern. Every word here is the CLIENT'S own
    // published copy: custom.ingrediente (ingredient + what it does), custom.beneficii_cheie and
    // custom.mod_de_folosire, served live so the page always says what the product page says.
    // We never author clinical prose about the user's skin - PRD 12 forbids generating medical
    // benefit copy at runtime, and 10.2 restricts naming a condition at all.
    const panel = (title, body) => body ? `
      <details class="iva-quiz__detail">
        <summary>${esc(title)}</summary>
        <div class="iva-quiz__detail-body">${body}</div>
      </details>` : "";

    const stepPanels = (s) => {
      const ing = (s.ingredients || []).filter((i) => i && i.name && i.what_it_does);
      const steps = Array.isArray(s.usage_steps) ? s.usage_steps : [];
      // a degraded Shopify fetch leaves no client copy at all: render nothing, not an empty shell
      if (!ing.length && !s.benefits && !s.how_to_use && !steps.length) return "";
      // expanded state carries the client's own ingredients visual when they maintain one
      // (custom.imagine_ingrediente, 55 products), falling back to the second gallery shot
      const detailImg = s.ingredients_image || (Array.isArray(s.gallery) ? s.gallery[1] : null);
      return `<div class="iva-quiz__details">
        ${panel("Ce conține și de ce", ing.length
          ? `<dl class="iva-quiz__ing">${ing.map((i) =>
              `<dt>${escI(sentence(i.name))}</dt><dd>${escI(sentence(i.what_it_does))}</dd>`).join("")}</dl>` +
            (detailImg ? `<figure class="iva-quiz__detail-img"><img src="${esc(detailImg)}" alt="" loading="lazy" decoding="async"></figure>` : "")
          : "")}
        ${panel("Ce face pentru piele", s.benefits ? `<p>${escI(clip(s.benefits, 420))}</p>` : "")}
        ${panel("Cum se folosește", s.how_to_use || steps.length
          ? (steps.length
            ? `<ol class="iva-quiz__usage">${steps.map((x) => `<li>${escI(sentence(x))}</li>`).join("")}</ol>`
            : `<p>${escI(clip(s.how_to_use, 320))}</p>`)
          : "")}
      </div>`;
    };

    const insights = (p, many) => {
      const obs = (p.observations || []).map((o) => OBSERVATION_RO[o.key]).filter(Boolean);
      const objs = (p.objectives || []).map((o) => OBJECTIVE_RO[o]).filter(Boolean);
      if (!obs.length && !objs.length) return "";
      return `<section class="iva-quiz__insights">
        ${many ? `<h3 class="iva-quiz__insights-zone">${esc(p.track === "face" ? "Ten" : "Păr & scalp")}</h3>` : ""}
        ${obs.length ? `<div class="iva-quiz__insight">
          <h4>Ce am observat din răspunsurile tale</h4>
          <ul>${obs.slice(0, 3).map((t) => `<li>${esc(sentence(t))}</li>`).join("")}</ul></div>` : ""}
        ${objs.length ? `<div class="iva-quiz__insight">
          <h4>Obiectivele rutinei</h4>
          <ul>${objs.slice(0, 4).map((t) => `<li>${esc(sentence(t))}</li>`).join("")}</ul></div>` : ""}
      </section>`;
    };

    const sellableOf = (p) => p.clinical_state !== "consult_first";
    const routines = (p, label) => {
      if (!p) return "";
      // When the morning and evening sets are IDENTICAL, two sections say nothing: render one,
      // "Dimineața și seara". The split only appears when it carries information - an SPF that is
      // morning-only, or an acid the engine schedules in the evening (Julian, 17 Aug).
      // The hair routine has no morning/evening split at all (client, 19 Aug): the shampoo and
      // the scalp serum are each used once a day, so "Dimineața"/"Seara" headings invent a
      // schedule the client does not recommend. One plain section instead.
      if (p.track === "hair_scalp") {
        return `
      ${label ? `<h2 class="iva-quiz__title" style="text-align:left;font-size:2.4rem;margin-top:4rem">${esc(label)}</h2>` : ""}
      ${daypart(p.routine.map((s) => ({ ...s, am: true, pm: false })), "Rutina zilnică", "am", sellableOf(p))}`;
      }
      const amS = p.routine.filter((s) => s.am);
      const pmS = p.routine.filter((s) => s.pm);
      const identical = amS.length > 0 && amS.length === pmS.length && amS.every((s) => s.pm);
      const sellable = sellableOf(p);
      // Three shapes (Julian, 17 Aug): identical sets -> ONE combined section; a daypart that is
      // NOTHING BUT repeats of the other -> one sentence, not a column of ghost cards ("Seara"
      // listing three "același produs ca dimineața" rows says nothing); genuinely different
      // dayparts -> the full split, because then it carries information.
      const amOnly = p.routine.filter((s) => s.am && !s.pm);
      const pmOnly = p.routine.filter((s) => s.pm && !s.am);
      const noteFor = (title, missing) => `<section class="iva-quiz__daypart iva-quiz__daypart--note">
          <h2>${esc(title)}</h2>
          <p>Aceeași rutină ca ${title === "Seara" ? "dimineața" : "seara"}${missing.length
            ? `, fără ${esc(missing.map((s) => (SLOT_RO[s.slot] || s.slot).toLowerCase()).join(" și "))}`
            : ""}.</p>
        </section>`;
      const body = identical
        ? daypart(p.routine.map((s) => ({ ...s, pm: false })), "Dimineața și seara", "am", sellable)
        : pmOnly.length === 0
        ? `${daypart(p.routine, "Dimineața", "am", sellable)}${noteFor("Seara", amOnly)}`
        : amOnly.length === 0
        ? `${noteFor("Dimineața", pmOnly)}${daypart(p.routine, "Seara", "pm", sellable)}`
        : `${daypart(p.routine, "Dimineața", "am", sellable)}${daypart(p.routine, "Seara", "pm", sellable)}`;
      return `
      ${label ? `<h2 class="iva-quiz__title" style="text-align:left;font-size:2.4rem;margin-top:4rem">${esc(label)}</h2>` : ""}
      ${body}`;
    };

    const jovialeSetting = (cfg.settings || {}).joviale;
    const JOVIALE = jovialeSetting === false ? null : jovialeSetting || {
      name: "Joviale Fond de Ten Fluid SPF50+",
      price: 143.90,
      url: "https://ivatherm.ro/products/joviale-fond-de-ten-fluid-spf50",
      image: "https://cdn.shopify.com/s/files/1/0943/3120/8024/files/IVATHERM_Joviale_Vanilla_1.jpg?v=1776154543&width=400",
      variants: [
        { id: 57007906783576, title: "01 Milk", img: "https://cdn.shopify.com/s/files/1/0943/3120/8024/files/IVATHERM_Joviale_Milk.jpg?width=300" },
        { id: 57007906816344, title: "02 Vanilla", img: "https://cdn.shopify.com/s/files/1/0943/3120/8024/files/IVATHERM_Joviale_Vanilla.jpg?width=300" },
        { id: 57007906849112, title: "03 Caramel", img: "https://cdn.shopify.com/s/files/1/0943/3120/8024/files/IVATHERM_Joviale_Caramel.jpg?width=300" },
      ],
    };
    const jovialeCard = face && JOVIALE ? `
      <aside class="iva-quiz__nicetohave">
        <p class="iva-quiz__nicetohave-kicker">Se potrivește cu orice rutină</p>
        <img class="iva-quiz__nicetohave-img" data-joviale-img src="${esc(JOVIALE.image)}" alt="${esc(JOVIALE.name)}" loading="lazy" decoding="async">
        <p class="iva-quiz__nicetohave-name">${esc(JOVIALE.name)}</p>
        <p class="iva-quiz__nicetohave-shade" data-joviale-shade hidden></p>
        <button type="button" class="iva-quiz__toggle iva-quiz__nicetohave-cta" data-joviale-add aria-pressed="false">Adaugă la rutină</button>
        <a class="iva-quiz__nicetohave-change" data-joviale-change href="${esc(JOVIALE.url)}" target="_blank" rel="noopener" hidden>Schimbă nuanța</a>
      </aside>` : "";

    const dims = (main ? main.dimensions : []).filter((d) => d.band !== "balanced" && d.band !== "not_assessed");
    const bundleCard = r.bundle.eligible ? `
      <div class="iva-quiz__asidecol">
      <aside class="iva-quiz__bundle">
        <h3>${esc((cfg.settings || {}).bundle_title || "Rutina ta completă")}</h3>
        <p class="iva-quiz__bundle-hint">Poți debifa oricând produsele pe care nu le vrei; comanda include doar ce lași bifat.</p>
        <p class="iva-quiz__bundle-trust">${esc((cfg.settings || {}).bundle_note || "Fiecare produs a trecut regulile de siguranță ale evaluării: tratamente prescrise, toleranță și incompatibilități.")}</p>
        ${(cfg.settings || {}).shipping_note ? `<p class="iva-quiz__bundle-trust"><b>${esc((cfg.settings || {}).shipping_note)}</b></p>` : ""}
        <button type="button" class="iva-quiz__btn iva-quiz__btn--primary" data-add-bundle>
          <span data-bundle-label>${r.page_availability_state === "partial_stock" ? "Adaugă produsele disponibile" : "Adaugă rutina"}</span>
        </button>
        <p class="iva-quiz__bundle-count" data-bundle-count></p>
      </aside>${jovialeCard}
      </div>` : "";

    const hasRoutine = r.profiles.some((p) => p.routine.length > 0);
    // Headline follows the actual outcome: a consult / no-match result must never open with
    // "your routine is ready" and an empty grid (that was the broken state Julian caught).
    const HEAD = {
      consult_first: ["Îți recomandăm un consult dermatologic.", "Răspunsurile tale indică o situație care merită văzută de un medic. Până atunci, doar îngrijire blândă de suport, fără produse active."],
      no_match: ["Nu am găsit o potrivire sigură.", "Preferăm să nu recomandăm la întâmplare. Poți relua evaluarea sau ne poți scrie."],
      support_only: ["Rutina ta de suport este gata.", "Completează tratamentul prescris, fără să îl înlocuiască. Fiecare produs are un motiv, îl vezi lângă el."],
      minimal_tolerance: ["Rutina ta esențială este gata.", "Am păstrat doar pașii de bază, cu produse cu toleranță ridicată."],
    };
    // Regulatory block (sarcină / alăptare) outranks every other state. It is also the only
    // consult case with a genuinely EMPTY routine, so the standard consult_first copy ("mai jos
    // îți arătăm doar îngrijire blândă de suport") would promise products that are not there.
    // Two regulatory outcomes, and they say opposite things, so they must not share copy:
    //   regulatory_pregnancy            -> strict mode, nothing recommended at all
    //   regulatory_pregnancy_disclaimer -> a routine IS shown, with retinoids and acids removed
    const regBlocked = r.profiles.some((p) => (p.safety_messages || []).includes("regulatory_pregnancy"));
    const head = regBlocked
      ? ["Îți recomandăm un consult medical.",
         "Ai spus că ești însărcinată sau că alăptezi. În aceste situații nu îți putem recomanda produse, indiferent care sunt ele. Un medic îți poate spune ce este potrivit pentru tine acum."]
      : HEAD[r.page_clinical_state] ?? (hasRoutine
      ? ["Evaluarea este gata.", "Rutina de mai jos este construită din răspunsurile tale. Fiecare produs are un motiv, îl vezi lângă el."]
      : HEAD.no_match);

    const dimList = (main ? main.dimensions : []).filter((d) => d.band !== "not_assessed");
    const heroMedia = (cfg.media || {})[hasRoutine ? "RESULT" : "CONSULT"] ?? (cfg.media || {}).RESULT;

    // concerns, in the user's own priority order (primary first, then the loop rounds)
    // Client (Ana, 25 Aug): the result read "ne concentrăm pe: undetermined". The old fallback
    // chain ended in `|| c`, which printed the RAW ENGINE KEY when neither dictionary had it.
    // "undetermined" is not a concern, it is the engine saying it could not name one, and it is
    // what the consult path produces: 41 of 817 profiles in the last 30 days, about 5%.
    // Never print an unmapped key. Drop it instead; the lead line below is already conditional,
    // so a profile with nothing nameable simply loses the line rather than showing English.
    const concernList = r.profiles.flatMap((p) => [p.title?.primary, ...(p.title?.secondary ? [p.title.secondary] : [])])
      .filter(Boolean).map((c) => CONCERN_RO[c] || SECONDARY_RO[c] || null).filter(Boolean);
    const concerns = [...new Set(concernList)].slice(0, 3);

    const frag = h(`
    <div class="iva-quiz__result">
      <div class="iva-quiz__result-hero${heroMedia && heroMedia.active !== false ? " iva-quiz__result-hero--image" : ""}">
        ${heroMedia && heroMedia.active !== false ? `<img class="iva-quiz__result-bg" src="${esc(heroMedia.img)}" alt="" aria-hidden="true">` : ""}
        <div class="iva-quiz__result-hero-inner">
          ${dimList.length ? `<div class="iva-quiz__badge">
            <p class="iva-quiz__badge-label">Profilul tău</p>
            <dl>${dimList.slice(0, 3).map((d) => `
              <div><dt>${esc(DIM_RO[d.key] || d.key)}</dt><dd>${esc(BAND_RO[d.band])}</dd></div>`).join("")}</dl>
          </div>` : ""}
          <div class="iva-quiz__result-intro">
            <p class="iva-quiz__result-kicker">${esc(head[0])}</p>
            <h1 data-focus>${r.profiles.map(profileTitle).map(esc).join(" · ")}</h1>
            ${concerns.length ? `<p class="iva-quiz__result-lead">Pe baza răspunsurilor tale, ne concentrăm pe: ${esc(concerns.join(", "))}.</p>` : ""}
            <div class="iva-quiz__result-narrative" data-narrative>
              <p>${esc(narrative(r, main))}</p>
              ${hasRoutine ? `<p class="iva-quiz__result-more">${esc(head[1])}</p>` : ""}
            </div>
            <div class="iva-quiz__result-actions">
              ${hasRoutine ? `<button type="button" class="iva-quiz__btn iva-quiz__btn--onphoto" data-see-routine>Vezi rutina</button>` : ""}
              <button type="button" class="iva-quiz__readmore" data-readmore>Citește mai mult</button>
            </div>
            <p class="iva-quiz__result-disclaimer">${esc((cfg.settings || {}).result_disclaimer || "Recomandare dermatocosmetică, nu un diagnostic. Nu înlocuiește consultul medicului dermatolog.")}</p>
            <p class="iva-quiz__result-disclaimer">Dacă ești însărcinată sau alăptezi, recomandăm consultarea medicului înainte de introducerea oricărui produs nou în rutina de îngrijire și evitarea produselor care conțin retinoizi.</p>
          </div>
        </div>
      </div>
      <div class="iva-quiz__result-body">
        <div>
          ${safetyStates}
          ${r.profiles.map((p) => routines(p, r.profiles.length > 1 ? (p.track === "face" ? "Ten" : "Păr & scalp") : "")).join("")}
          ${hasRoutine ? "" : deadEndBody(r)}
        </div>
        ${hasRoutine ? bundleCard : deadEndAside(r)}
      </div>
      ${explainerSection(r, r.profiles.map((p) => insights(p, r.profiles.length > 1)).join("") + advisoryNotes)}
      ${raritySection(r)}
      ${emailBlock(hasRoutine)}
    </div>`);

    // selection state: every recommended product starts in the routine and can be removed/re-added.
    // A consult_first zone is displayed but never sellable (AC-M05), so its gentle support is not
    // part of the add-all selection, the count or the total. Interactions (quick view, panels)
    // still need EVERY rendered step - building them from the sellable list left consult cards
    // with dead buttons (review UI-02).
    const allSteps = r.profiles.flatMap((p) => p.routine);
    const steps = r.profiles.filter((p) => p.clinical_state !== "consult_first").flatMap((p) => p.routine);
    const chosen = new Set(steps.filter((s) => s.available !== false).map((s) => s.sku_id));
    const money2 = (n) => `${n.toFixed(2).replace(".", ",")} ${esc(steps[0]?.currency || r.bundle.currency || "RON")}`;
    const addBtn = frag.querySelector("[data-add-bundle]");
    const label = frag.querySelector("[data-bundle-label]");
    const countEl = frag.querySelector("[data-bundle-count]");

    let jovialeShade = null; // {id, title, img} once the user picked a nuance
    function syncBundle() {
      if (!addBtn) return;
      const picked = steps.filter((s) => chosen.has(s.sku_id));
      const total = picked.reduce((sum, s) => sum + Number(s.price ?? 0), 0) +
        (jovialeShade ? Number(JOVIALE.price) : 0);
      const base = r.page_availability_state === "partial_stock"
        ? "Adaugă produsele disponibile"
        : ((cfg.settings || {}).bundle_cta || "Adaugă rutina");
      label.textContent = total > 0 ? `${base} · ${money2(total)}` : base;
      const n = picked.length + (jovialeShade ? 1 : 0);
      countEl.textContent = n ? `${n} ${n === 1 ? "produs selectat" : "produse selectate"}` : "Niciun produs selectat";
      addBtn.disabled = n === 0;
    }

    // Joviale shade flow (Julian, 20 Aug): CTA opens a shade modal; the chosen variant joins the
    // routine and the cart. Never pre-checked - the client picks the nuance first.
    const jovialeBtn = frag.querySelector("[data-joviale-add]");
    const jovialeShadeEl = frag.querySelector("[data-joviale-shade]");
    const jovialeChange = frag.querySelector("[data-joviale-change]");
    const jovialeImg = frag.querySelector("[data-joviale-img]");
    function setJoviale(v) {
      jovialeShade = v;
      if (!jovialeBtn) return;
      jovialeBtn.setAttribute("aria-pressed", String(!!v));
      jovialeBtn.textContent = v ? "În rutină ✓" : "Adaugă la rutină";
      jovialeShadeEl.hidden = !v;
      if (v) { jovialeShadeEl.textContent = `Nuanța ${v.title} · ${money2(Number(JOVIALE.price))}`; if (v.img) jovialeImg.src = v.img; }
      else { jovialeImg.src = JOVIALE.image; }
      if (jovialeChange) {
        jovialeChange.hidden = !v;
        jovialeChange.onclick = (e) => { e.preventDefault(); openShadeModal(); };
      }
      syncBundle();
    }
    function openShadeModal() {
      const overlay = document.createElement("div");
      overlay.className = "iva-quiz__shades";
      overlay.innerHTML = `
        <div class="iva-quiz__shades-backdrop" data-close></div>
        <div class="iva-quiz__shades-panel" role="dialog" aria-modal="true" aria-label="Alege nuanța">
          <p class="iva-quiz__shades-title">Alege nuanța</p>
          <div class="iva-quiz__shades-grid">
            ${JOVIALE.variants.map((v, i) => `
              <button type="button" class="iva-quiz__shade" data-shade="${i}">
                <img src="${esc(v.img || JOVIALE.image)}" alt="${esc(v.title)}">
                <span>${esc(v.title)}</span>
              </button>`).join("")}
          </div>
          <button type="button" class="iva-quiz__shades-close" data-close aria-label="Închide">×</button>
        </div>`;
      const close = () => { overlay.remove(); document.removeEventListener("keydown", onKey); };
      const onKey = (e) => { if (e.key === "Escape") close(); };
      overlay.querySelectorAll("[data-close]").forEach((el) => el.addEventListener("click", close));
      overlay.querySelectorAll("[data-shade]").forEach((el) => el.addEventListener("click", () => {
        setJoviale(JOVIALE.variants[Number(el.dataset.shade)]);
        track("quiz_joviale_selected", { variant_id: jovialeShade.id });
        close();
      }));
      document.addEventListener("keydown", onKey);
      document.body.appendChild(overlay);
    }
    if (jovialeBtn) jovialeBtn.addEventListener("click", () => {
      if (jovialeShade) setJoviale(null); // pressed again = take it back out
      else openShadeModal();
    });

    frag.querySelectorAll("[data-toggle]").forEach((btn) => {
      const sku = btn.dataset.toggle;
      const step = steps.find((s) => s.sku_id === sku);
      if (step && step.available === false) { btn.disabled = true; btn.setAttribute("aria-pressed", "false"); }
      btn.addEventListener("click", () => {
        if (chosen.has(sku)) chosen.delete(sku); else chosen.add(sku);
        btn.setAttribute("aria-pressed", String(chosen.has(sku)));
        btn.closest(".iva-quiz__product")?.classList.toggle("is-removed", !chosen.has(sku));
        syncBundle();
      });
    });
    syncBundle();

    let addInFlight = false;
    if (addBtn) addBtn.addEventListener("click", () => {
      // every other add-to-cart path in this theme disables its button while in flight; without
      // it a double tap fires two /cart/add.js calls and adds the routine twice
      if (addInFlight) return;
      const items = steps.filter((s) => chosen.has(s.sku_id)).map((s) => ({ id: s.shopify_variant_id, quantity: 1 }));
      if (jovialeShade) items.push({ id: jovialeShade.id, quantity: 1 });
      if (!items.length) return;
      addInFlight = true; addBtn.disabled = true;
      setTimeout(() => { addInFlight = false; if (addBtn) addBtn.disabled = false; }, 4000);
      track("quiz_bundle_added", { eligible_count: steps.length, added_count: items.length,
        partial_stock: r.page_availability_state === "partial_stock" });
      // keepalive: the Liquid adapter follows this with window.location = /cart, which aborts an
      // ordinary fetch. This is the last event before the user leaves the page and the one the
      // add-rate KPI is built on, so it is the least affordable event in the funnel to drop.
      flushEvents(true);
      // 2nd arg is display-only sugar for hosts that render their own drawer. The contract is
      // still items:[{id, quantity}] exactly as /cart/add.js wants it, so a Liquid adapter can
      // ignore this argument entirely.
      // third arg: ids the Liquid adapter stamps onto the cart so the resulting Shopify order can
      // be attributed back to this run. Purely additive - the adapter ignores it if absent.
      cfg.cart.add(items, steps.filter((s) => chosen.has(s.sku_id)), {
        result_id: S.result?.result_id,
        session_key: S.sessionKey,
      });
    });

    wireEmailBlock(frag, r);

    // quick view (Typology's VIEW MORE): stays inside the result, never navigates away
    frag.querySelectorAll("[data-quick]").forEach((el) => {
      el.addEventListener("click", () => {
        const step = allSteps.find((s) => s.sku_id === el.dataset.quick);
        track("quiz_product_clicked", { product_id: step?.sku_id, routine_slot: step?.slot, rank: steps.indexOf(step) + 1 });
        openQuickView(step);
      });
    });
    const more = frag.querySelector("[data-readmore]");
    // Capture the node NOW. h() returns a DocumentFragment and render() appendChild()s it, which
    // MOVES the children out and leaves the fragment EMPTY - so any frag.querySelector deferred
    // into a callback returns null. That is exactly why this button did nothing: the click handler
    // re-queried the emptied fragment and threw on null.classList. Element references survive the
    // move; the fragment does not.
    const narrativeBox = frag.querySelector("[data-narrative]");
    if (more && narrativeBox) {
      more.addEventListener("click", () => {
        const open = narrativeBox.classList.toggle("is-open");
        more.textContent = open ? "Mai puțin" : "Citește mai mult";
      });
      // client, 21 Aug: "Butonul CITESTE MAI MULT nu duce nicaieri" - when the narrative already
      // fits its clamp there is nothing to reveal, so the button should not exist. This check was
      // dead for the same reason, which is why the button appeared regardless.
      requestAnimationFrame(() => {
        if (narrativeBox.scrollHeight <= narrativeBox.clientHeight + 6) more.hidden = true;
      });
    }
    // the hero's primary action: get to the actual routine without hunting for it
    const see = frag.querySelector("[data-see-routine]");
    // look up from the live DOM at click time: `frag` is a DocumentFragment and is emptied the
    // moment render() moves its children into the slide, so querying it here would find nothing
    if (see) see.addEventListener("click", (e) => {
      const root = e.currentTarget.closest(".iva-quiz__result");
      const target = root?.querySelector(".iva-quiz__daypart") || root?.querySelector(".iva-quiz__result-body");
      target?.scrollIntoView({ behavior: REDUCE_MOTION ? "auto" : "smooth", block: "start" });
    });

    const restart = frag.querySelector("[data-restart]");
    if (restart) restart.addEventListener("click", () => {
      S.answers = {}; S.trail = []; S.totalSeen = 0; S.pctSeen = 0; S.result = null; S.resultPromise = null;
      S.emailDone = false; clearLocal();
      // NOT rotating the session key here, though an earlier draft did. Rotation was aimed at the
      // 23505 on results_dedupe_uq, but it closed only this route (observed incidence: zero) while
      // leaving the likelier one open - [data-retry] after a dropped connection re-POSTs identical
      // answers under the same key - and it inflated quiz_funnel.started, which counts distinct
      // session_keys, by one per restart. quiz-recommendations now handles the 23505 directly by
      // reusing the existing row's id when its bundle is identical, which covers every route
      // without touching a KPI.
      S.dir = "back"; render(screenLanding());
    });
    return frag;
  }

  // Save-by-email step, shown BEFORE the reveal (Typology position) but NEVER a gate:
  // Continuă always works, with or without an address (PRD §4.2/§12.2, client Decision 9).
  function screenEmail() {
    const st = cfg.settings || {};
    const frag = h(`
    <div class="iva-quiz__screen">
      ${media("EMAIL")}
      <div class="iva-quiz__panel"><div class="iva-quiz__card iva-quiz__card--email">
        ${brandMark()}
        <h2 class="iva-quiz__title" data-focus>${esc(st.email_title || "Vrei să păstrezi rezultatul?")}</h2>
        <p class="iva-quiz__helper">${esc(st.email_sub || "Îți trimitem rutina pe email, ca să o ai la îndemână.")}</p>
        <label class="iva-quiz__sr" for="iva-quiz-email">Adresa de email</label>
        <input class="iva-quiz__input" id="iva-quiz-email" type="email" inputmode="email" autocomplete="email" placeholder="nume@exemplu.ro">
        <label class="iva-quiz__check"><input type="checkbox" data-consent="save"> <span>${esc(st.consent_save || "Trimiteți-mi rezultatul pe email. Îl păstrăm 30 de zile.")}</span></label>
        <label class="iva-quiz__check"><input type="checkbox" data-consent="marketing"> <span>${esc(st.consent_marketing || "Vreau să primesc și noutăți și oferte Ivatherm. (opțional)")}</span></label>
        <div class="iva-quiz__nav">
          <button type="button" class="iva-quiz__btn iva-quiz__btn--ghost" data-back>Înapoi</button>
          <button type="button" class="iva-quiz__btn iva-quiz__btn--primary" data-email-continue>Vezi rezultatul</button>
        </div>
        <p class="iva-quiz__legal" data-email-msg>${esc(st.email_legal || "Rezultatul se vede și fără email. Salvarea rezultatului și comunicările de marketing sunt două acorduri separate, pe care le poți retrage oricând.")}</p>
      </div></div>
    </div>`);

    const input = frag.querySelector("#iva-quiz-email");
    const save = frag.querySelector('[data-consent="save"]');
    const marketing = frag.querySelector('[data-consent="marketing"]');
    const msg = frag.querySelector("[data-email-msg]");
    const go = frag.querySelector("[data-email-continue]");
    frag.querySelector("[data-back]").addEventListener("click", () => { S.emailDone = false; back(); });

    // ticking an address implies you want it saved; the marketing tick stays independent
    input.addEventListener("input", () => { if (input.value.trim() && !save.checked) save.checked = true; });

    go.addEventListener("click", async () => {
      go.disabled = true;
      const email = input.value.trim();
      if (email && save.checked) {
        msg.textContent = "Se trimite…";
        try {
          const r = await (S.resultPromise ?? fetchResult());
          await api("/quiz-email", {
            method: "POST",
            body: JSON.stringify({
              email,
              result_id: r.result_id,
              session_key: S.sessionKey,          // proves the result is ours (IDOR fix)
              consent_save_result: true,
              consent_marketing: marketing.checked,
            }),
          });
        } catch { /* saving must never block the reveal */ }
      }
      S.emailDone = true;
      submit();
    });
    return frag;
  }

  // "De ce acest produs + cum se integrează" (PRD 10.1). Approved per-SKU copy wins when the
  // catalog carries it; otherwise we compose a neutral line from the match + the routine slot.
  function explainStep(s) {
    if (s.explanation?.template) return s.explanation.template;
    const concern = s.explanation?.concern && (CONCERN_RO[s.explanation.concern] || s.explanation.concern);
    const role = roleOf(s).toLowerCase();
    const when = WHEN_RO[s.explanation?.when || (s.am && s.pm ? "both" : s.am ? "am" : "pm")];
    return concern
      ? `L-am inclus pentru ${concern}. Rolul lui în rutină: ${role}, ${when}.`
      : `Rolul lui în rutină: ${role}, ${when}.`;
  }

  // consult_first / no_match are designed states, not error pages (PRD 10.3: the medical
  // recommendation precedes the commercial CTA - it does not mean an empty screen).
  const CONSULT_STEPS = [
    ["Ce înseamnă", "Unele semne se evaluează cel mai bine direct, de un medic dermatolog. Nu îți propunem produse active pentru ele."],
    ["Ce urmează", "La consult, descrie ce ai observat: de când, cât de des, ce ai folosit până acum."],
    ["Până atunci", "Păstrează o rutină simplă și blândă și evită produsele noi cu ingrediente active."],
  ];
  const NOMATCH_STEPS = [
    ["De ce s-a întâmplat", "Combinația răspunsurilor tale nu ne-a permis să compunem o rutină în care avem încredere."],
    ["Ce poți face", "Reia evaluarea, dacă vrei să ajustezi câteva răspunsuri, sau scrie-ne și îți răspunde echipa noastră."],
    ["Alternativ", "Un consult dermatologic rămâne cea mai sigură cale dacă simptomele persistă."],
  ];

  function deadEndBody(r) {
    const consult = r.page_clinical_state === "consult_first";
    const steps = consult ? CONSULT_STEPS : NOMATCH_STEPS;
    return `
      <section class="iva-quiz__steps">
        ${steps.map(([h, p], i) => `
          <article class="iva-quiz__step">
            <span class="iva-quiz__step-n">${String(i + 1).padStart(2, "0")}</span>
            <div><h4>${esc(h)}</h4><p>${esc(p)}</p></div>
          </article>`).join("")}
      </section>`;
  }

  function deadEndAside(r) {
    const st = cfg.settings || {};
    const consult = r.page_clinical_state === "consult_first";
    // We RECOMMEND a dermatologist (PRD 2.1 "direcționat către dermatolog"); we do not offer,
    // book or broker medical consultations - Ivatherm has no such service. Copy stays advisory,
    // and we point at whatever approved page the client configures.
    return `
      <aside class="iva-quiz__bundle iva-quiz__bundle--consult">
        <h3>${esc(consult ? "Vezi un medic dermatolog" : "Hai să te ajutăm")}</h3>
        <p class="iva-quiz__bundle-trust">${esc(consult
          ? "Recomandarea noastră este să discuți cu un medic dermatolog. Notează ce am observat mai sus, ca să îi poți descrie ușor situația."
          : "Echipa noastră poate parcurge împreună cu tine ce ai observat.")}</p>
        ${consult && st.consult_url ? `<a class="iva-quiz__btn iva-quiz__btn--primary" href="${esc(st.consult_url)}">${esc(st.consult_cta || "Unde poți merge")}</a>` : ""}
        ${!consult ? `<a class="iva-quiz__btn iva-quiz__btn--primary" href="${esc(st.contact_url || "/pages/contact")}">Scrie-ne</a>` : ""}
        <button type="button" class="iva-quiz__btn iva-quiz__btn--ghost" data-restart>Reia evaluarea</button>
      </aside>`;
  }

  // "Cum am construit profilul": what each dimension measures and where this answer set sits on
  // it (PRD 10.1 "priorități vizuale pe dimensiuni", numeric score stays behind its feature flag).
  const DIM_EXPLAIN = {
    hydration: "Cât de bine reține pielea apa pe parcursul zilei.",
    comfort: "Cât de ușor reacționează pielea la produse și factori externi.",
    barrier: "Cât de intactă este bariera care protejează pielea.",
    uniformity: "Cât de uniform este tonul: pete, roșeață, urme.",
    sebum: "Cât sebum produce pielea și cum apar imperfecțiunile.",
    firmness: "Fermitatea și vitalitatea pielii.",
    density: "Densitatea părului și ancorarea firului.",
    fiber: "Rezistența firului de păr la rupere.",
    scalp_balance: "Echilibrul scalpului: descuamare, sebum, confort.",
  };
  const BANDS = ["balanced", "mild", "moderate", "priority"];

  function explainerSection(r, insightsHtml) {
    const rows = r.profiles.map((p) => {
      const dims = (p.dimensions || []).filter((d) => d.band !== "not_assessed");
      if (!dims.length) return "";
      return `
      <div class="iva-quiz__scales">
        ${r.profiles.length > 1 ? `<h4>${esc(p.track === "face" ? "Ten" : "Păr & scalp")}</h4>` : ""}
        ${dims.map((d) => `
          <div class="iva-quiz__scale">
            <div class="iva-quiz__scale-head">
              <b>${esc(DIM_RO[d.key] || d.key)}</b>
              <span>${esc(DIM_EXPLAIN[d.key] || "")}</span>
            </div>
            <div class="iva-quiz__scale-track" role="img" aria-label="${esc((DIM_RO[d.key] || d.key) + ": " + (BAND_RO[d.band] || d.band))}">
              ${BANDS.map((b) => `<i class="${b === d.band ? "is-on" : ""}"></i>`).join("")}
            </div>
            <div class="iva-quiz__scale-labels">${BANDS.map((b) => `<span class="${b === d.band ? "is-on" : ""}">${esc(BAND_RO[b])}</span>`).join("")}</div>
          </div>`).join("")}
      </div>`;
    }).join("");
    if (!rows.trim()) return "";
    const st = cfg.settings || {};
    return `
    <section class="iva-quiz__explainer">
      <h2 class="iva-quiz__explainer-title">${esc((cfg.settings || {}).diagnostic_title || "Profilul tău")}</h2>
      <div class="iva-quiz__explainer-inner">
        <div class="iva-quiz__explainer-intro">
          <p>Profilul tău este descris pe câteva dimensiuni, nu printr-un scor clinic:</p>
          <div class="iva-quiz__criteria">
            <div><h4>Ce ai semnalat</h4><p>Fiecare dimensiune se calculează doar din întrebările pe care le-ai văzut și la care ai răspuns.</p></div>
            <div><h4>Cât de sigur este răspunsul</h4><p>Opțiunile „nu sunt sigur(ă)" nu adaugă severitate; dimensiunile fără date rămân neevaluate.</p></div>
            <div><h4>Ce nu facem</h4><p>Nu punem diagnostice și nu recomandăm produse peste un tratament prescris fără să îți spunem.</p></div>
          </div>
          <p class="iva-quiz__explainer-note">Evaluare dermatocosmetică. Nu stabilim un diagnostic și nu înlocuim consultul medicului dermatolog.</p>
        </div>
        <div class="iva-quiz__explainer-scales">${rows}${insightsHtml || ""}</div>
      </div>
      ${st.philosophy ? `<div class="iva-quiz__philosophy"><h3>Filosofia noastră</h3><p>${esc(st.philosophy)}</p></div>` : ""}
    </section>`;
  }

  // Shown only when the engine says the sample is large enough to say anything (PRD 12.2:
  // aggregate reporting with a minimum volume threshold).
  function raritySection(r) {
    if (!r.rarity || !r.rarity.percent) return "";
    const media = (cfg.media || {}).F12_SECONDARY_CONCERNS;
    return `
    <section class="iva-quiz__rarity">
      <div class="iva-quiz__rarity-media">${media ? `<img src="${esc(media.img)}" alt="" loading="lazy">` : ""}</div>
      <div class="iva-quiz__rarity-text">
        <p class="iva-quiz__result-kicker">Profilul tău</p>
        <h2>Ești ca ${esc(String(r.rarity.percent))}% dintre cei care au făcut evaluarea.</h2>
      </div>
    </section>`;
  }

  function emailBlock(hasRoutine) {
    const st = cfg.settings || {};
    const enabled = S.versions.email_capture_enabled || st.preview_email_capture;
    if (!enabled || (st.email_step || "after") !== "after") return "";
    // Nothing to send, so do not offer to send it. Ana, 26 Aug, on pregnancy and breastfeeding:
    // "nu recomandam niciun produs ... therefore nu o sa fie nicio rutina de trimis pe email."
    // The server already refuses to send an empty routine; without this the box would still
    // appear, the user would submit, and the page would say "Ți-am trimis rezultatul pe email"
    // while nothing ever arrives.
    if (hasRoutine === false) return "";
    return `
    <section class="iva-quiz__email">
      <div class="iva-quiz__email-card">
        <h2 class="iva-quiz__title">${esc(st.email_title || "Vrei să păstrezi rezultatul?")}</h2>
        <p class="iva-quiz__helper">${esc(st.email_sub || "Îți trimitem rutina pe email, ca să o ai la îndemână.")}</p>
        <label class="iva-quiz__sr" for="iva-quiz-email">Adresa de email</label>
        <input class="iva-quiz__input" id="iva-quiz-email" type="email" inputmode="email" autocomplete="email" placeholder="nume@exemplu.ro">
        <label class="iva-quiz__check"><input type="checkbox" data-consent="save"> <span>${esc(st.consent_save || "Trimiteți-mi rezultatul pe email. Îl păstrăm 30 de zile.")}</span></label>
        <label class="iva-quiz__check"><input type="checkbox" data-consent="marketing"> <span>${esc(st.consent_marketing || "Vreau să primesc și noutăți și oferte Ivatherm. (opțional)")}</span></label>
        ${(S.versions.reassessment_enabled || st.preview_reassessment) ? `<label class="iva-quiz__check"><input type="checkbox" data-consent="reassessment"> <span>${esc(st.consent_reassessment || "Amintește-mi peste ~8 săptămâni să reevaluez rutina. (opțional)")}</span></label>` : ""}
        <button type="button" class="iva-quiz__btn iva-quiz__btn--primary" data-email-send disabled>Trimite rezultatul</button>
        <p class="iva-quiz__legal" data-email-msg>${esc(st.email_legal || "Rezultatul rămâne vizibil și fără email. Salvarea rezultatului și comunicările de marketing sunt două acorduri separate, pe care le poți retrage oricând.")}</p>
      </div>
    </section>`;
  }

  function wireEmailBlock(frag, r) {
    const input = frag.querySelector("#iva-quiz-email");
    if (!input) return;
    const send = frag.querySelector("[data-email-send]");
    const save = frag.querySelector('[data-consent="save"]');
    const marketing = frag.querySelector('[data-consent="marketing"]');
    // captured here, not inside the async send handler: by the time that runs the fragment has
    // been emptied by render(), so the old inline lookup returned null and !!undefined sent
    // reassessment as false regardless of what the user actually ticked
    const reassess = frag.querySelector('[data-consent="reassessment"]');
    const msg = frag.querySelector("[data-email-msg]");
    const sync = () => { send.disabled = !(/.+@.+\..+/.test(input.value.trim()) && save.checked); };
    input.addEventListener("input", () => { if (input.value.trim() && !save.checked) save.checked = true; sync(); });
    save.addEventListener("change", sync);
    send.addEventListener("click", async () => {
      send.disabled = true;
      msg.textContent = "Se trimite…";
      try {
        const res = await api("/quiz-email", {
          method: "POST",
          body: JSON.stringify({
            email: input.value.trim(),
            result_id: r.result_id,
            session_key: S.sessionKey,
            consent_save_result: true,
            consent_marketing: marketing.checked,
            reassessment: !!reassess?.checked,
          }),
        });
        if (!res.ok) throw new Error(String(res.status));
        const out = await res.json().catch(() => ({}));
        track("quiz_email_opt_in", { consent_version: out.consent_version });
        flushEvents(true);
        // quiz-email answers 200 even when it did NOT send: delivery is "queued" only on a real
        // send, and "pending_setup" when it had no routine to attach (no linked result row) so
        // Klaviyo refused an empty email. Claiming "we sent it" there is a lie the customer acts
        // on - they wait for an email that will never arrive - so say what actually happened.
        msg.textContent = out.delivery === "queued"
          ? "Gata. Ți-am trimis rezultatul pe email."
          // Do NOT promise delivery here. delivery is "pending_setup" or "failed", and there is no
          // retry queue anywhere in the system, so nothing will arrive without manual action.
          // Saying "we'll send it shortly" would just be a politer version of the same lie.
          : "Ți-am salvat adresa. Emailul cu rutina nu a putut fi trimis acum.";
        input.disabled = save.disabled = marketing.disabled = true;
      } catch {
        msg.textContent = "Nu am putut trimite emailul. Încearcă din nou.";
        send.disabled = false;
      }
    });
  }

  function clip(text, max) {
    if (!text) return "";
    const t = String(text).replace(/\s+/g, " ").trim();
    if (t.length <= max) return t;
    const cut = t.slice(0, max);
    const end = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf(", "), cut.lastIndexOf(" "));
    return cut.slice(0, end > 60 ? end : max).replace(/[,;:]$/, "") + "…";
  }

  function openQuickView(step) {
    if (!step) return;
    const gallery = (step.gallery && step.gallery.length ? step.gallery : [step.image_2x || step.image]).filter(Boolean);
    let i = 0;
    const wrap = document.createElement("div");
    wrap.className = "iva-quiz__modal";
    wrap.setAttribute("role", "dialog");
    wrap.setAttribute("aria-modal", "true");
    wrap.setAttribute("aria-label", step.product_name);
    wrap.innerHTML = `
      <div class="iva-quiz__modal-backdrop" data-close></div>
      <div class="iva-quiz__modal-card">
        <button type="button" class="iva-quiz__modal-close" data-close aria-label="Închide">×</button>
        <div class="iva-quiz__modal-media">
          ${gallery.length ? `<img data-gal src="${esc(gallery[0])}" alt="${esc(step.image_alt || step.product_name)}">` : ""}
          ${gallery.length > 1 ? `<button type="button" class="iva-quiz__modal-arrow iva-quiz__modal-arrow--prev" data-gal-prev aria-label="Imaginea anterioară">‹</button>
            <button type="button" class="iva-quiz__modal-arrow iva-quiz__modal-arrow--next" data-gal-next aria-label="Imaginea următoare">›</button>
            <div class="iva-quiz__modal-dots">${gallery.map((_, n) => `<i${n === 0 ? ' class="is-on"' : ""}></i>`).join("")}</div>` : ""}
        </div>
        <div class="iva-quiz__modal-body">
          <span class="iva-quiz__chip">${esc(roleOf(step))}</span>
          <h3 data-focus tabindex="-1">${esc(step.product_name)}</h3>
          <p class="iva-quiz__modal-meta">
            ${step.rating ? `<span>★ ${esc(Number(step.rating).toFixed(1))}/5${step.rating_count ? ` · ${esc(String(step.rating_count))} recenzii` : ""}</span>` : ""}
            ${step.size ? `<span>${esc(step.size)}</span>` : ""}
            ${step.price ? `<b>${esc(`${Number(step.price).toFixed(2).replace(".", ",")} ${step.currency || "RON"}`)}</b>` : ""}
          </p>
          ${step.actives ? `<p class="iva-quiz__modal-actives">${esc(step.actives)}</p>` : ""}
          ${step.short_description || step.description ? `<p class="iva-quiz__modal-desc">${escI(clip(step.short_description || step.description, 200))}</p>` : ""}
          ${step.matched_answer_ids.length ? `<p class="iva-quiz__modal-why"><b>De ce ți-l propunem:</b> ${esc(step.matched_answer_ids.map((m) => CONCERN_RO[m.split(":")[1]] || m.split(":")[1]).join(", "))}</p>` : ""}
          ${step.how_to_use ? `<details class="iva-quiz__modal-how"><summary>Cum se folosește</summary><p>${escI(clip(step.how_to_use, 260))}</p></details>` : ""}
          ${step.requires_medical_note ? `<p class="iva-quiz__modal-why">Produs cu recomandare de utilizare aprobată; verifică indicațiile de pe pagina produsului.</p>` : ""}
          <div class="iva-quiz__nav">
            <button type="button" class="iva-quiz__btn iva-quiz__btn--ghost" data-close>Înapoi</button>
            <a class="iva-quiz__btn iva-quiz__btn--primary" href="${esc(step.canonical_url)}" target="_blank" rel="noopener">Vezi produsul</a>
          </div>
        </div>
      </div>`;
    const close = () => { wrap.remove(); document.removeEventListener("keydown", onKey); };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    wrap.querySelectorAll("[data-close]").forEach((b) => b.addEventListener("click", close));
    const galImg = wrap.querySelector("[data-gal]");
    const show = (n) => {
      i = (n + gallery.length) % gallery.length;
      galImg.src = gallery[i];
      wrap.querySelectorAll(".iva-quiz__modal-dots i").forEach((d, k) => d.classList.toggle("is-on", k === i));
    };
    wrap.querySelector("[data-gal-next]")?.addEventListener("click", () => show(i + 1));
    wrap.querySelector("[data-gal-prev]")?.addEventListener("click", () => show(i - 1));
    document.addEventListener("keydown", onKey);
    // Stays on <body>: a transformed ancestor (the carousel animates with fill-mode both) would
    // become the containing block and break position:fixed. The CSS carries its own box-sizing
    // and font tokens instead of relying on the .iva-quiz scope.
    document.body.appendChild(wrap);
    wrap.querySelector("[data-focus]")?.focus();
  }

  function screenError(msg) {
    return h(`<div class="iva-quiz__screen">${media("ERROR")}
      <div class="iva-quiz__panel"><div class="iva-quiz__card" style="text-align:center">
      <h2 class="iva-quiz__title" data-focus>Ceva nu a mers.</h2>
      <p class="iva-quiz__helper">Răspunsurile tale sunt salvate local. (${esc(msg)})</p>
      <button type="button" class="iva-quiz__btn iva-quiz__btn--primary" data-retry>Încearcă din nou</button></div></div></div>`);
  }

  /* ---------- boot ---------- */
  root.addEventListener("click", (e) => {
    if (e.target.closest("[data-retry]")) submit();
  });
  api("/quiz-definition").then((r) => r.json()).then((d) => {
    S.def = d.definition;
    S.versions = d;
    const saved = loadLocal();
    if (saved && Object.keys(saved.answers || {}).length) {
      S.answers = pruneUnknownAnswers(saved.answers);
      // Validate: a browser that ran the OLD build stored a Math.random() float here, and this
      // line would overwrite the freshly minted v4 with it for the whole 24h TTL - defeating the
      // uuid() fix on exactly the browsers it exists for, and tripping the ownership check in
      // quiz-email. Keep the good key we already minted if the stored one is not a uuid.
      S.sessionKey = (typeof saved.session === "string" && UUID_RE.test(saved.session))
        ? saved.session : S.sessionKey;
      // restore the presentation trail; fall back to catalog order for runs saved before it existed
      S.trail = Array.isArray(saved.trail) && saved.trail.length
        ? saved.trail.filter((id) => byId(id))
        : visibleIds().filter((id) => S.answers[id] !== undefined);
      const resumeAt = (saved.current && byId(saved.current) && S.answers[saved.current] === undefined)
        ? saved.current
        : (nextUnanswered() || (saved.current && byId(saved.current) ? saved.current : null));
      // a resumed run never passes the start button, so startedAt was never set and
      // quiz_completed shipped no duration_ms. Stamp it here: the measured span is
      // "time from resuming to finishing", which is the honest number for a resumed run.
      // S.resumed marks the run so duration_ms stays interpretable: without it the column
      // silently mixes "start-click to finish" with "resume to finish" and the two populations
      // can never be separated again. The resumed measure is also inflated, because this stamps
      // at boot rather than on interaction, so idle time in a background tab counts.
      if (resumeAt) { S.startedAt = Date.now(); S.resumed = true; warm(); goTo(resumeAt); return; }
    }
    render(screenLanding());
  }).catch(() => render(screenError("nu am putut încărca întrebările")));
};
window.__ivaQuizBoot();
